import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { userSlice } from "../../redux/User";
import {
  axiosGet,
  axiosPut,
  axiosPost,
  axiosPatch,
} from "../../services/axiosService";
import "./onboarding.css";
import SearchBar from "@/components/shared/SearchBar";
import { Bg } from "react-flags-select";
import NoDataOnboard from "../agent/NoDataOnboard";
import { drop } from "lodash";
import { getAutoModeModelName } from "../cbb-builder/AiBotData";
import { useUserDetails } from "@/react-query/useUserDetails";
import useGetSingleCompany from "../cbb-builder/react-query/useGetSingleCompany";
import { GradientPlusIcon } from "@/assets/AiBotAssets";

/* ─── Provider / Model data ─────────────────────────────────────────────── */
const CUSTOM_MODEL_PROVIDER_ID = "custom-model";

const PROVIDERS = [
  {
    id: "auto",
    label: "Auto",
    description: "Built-in model selection",
    icon: "https://dlvkyia8i4zmz.cloudfront.net/47zjT8z5QPydTiEVuYfN_Frame__8___2_.svg",
    placeholder: "",
    background: "#F0F0F080",
  },
  {
    id: "openai",
    label: "OpenAI",
    description: "GPT-4o, GPT-4.1, o3-mini and more",
    icon: "https://dlvkyia8i4zmz.cloudfront.net/a5mOxIIS2eJeDCreYVpB_Vector.svg",
    placeholder: "sk-...",
    background: "#E7F5EE80",
  },
  {
    id: "google",
    label: "Google Gemini",
    description: "Gemini 2.5 Pro, Gemini 2.5 Flash and more",
    icon: " https://dlvkyia8i4zmz.cloudfront.net/mL9drK4QYuA0E2ndQ0PX_Icon_gemini.svg",
    placeholder: "AIza...",
    background: "#EEF0FF80",
  },
  {
    id: "anthropic",
    label: "Anthropic",
    description: "Claude 4 Sonnet, Claude 4.5 Haiku and more",
    icon: "https://dlvkyia8i4zmz.cloudfront.net/YQRr4qfQc2VqauQGPi6g_icon_anthropic.svg",
    placeholder: "sk-ant-...",
    background: "#F4EFEA80",
  },
  {
    id: "deepseek",
    label: "DeepSeek",
    description: "DeepSeek Chat V3 and more",
    icon: "https://dlvkyia8i4zmz.cloudfront.net/14mmMzyORaS0FZ38yD9f_Frame__11_.svg",
    placeholder: "sk-...",
    background: "#EAF2FF80",
  },
  {
    id: "groq",
    label: "Groq",
    description: "LLaMA 4 Maverick, Kimi K2 and more",
    icon: "https://dlvkyia8i4zmz.cloudfront.net/vU4PFf4RRgKVLEWz3PZf_groq_logo.svg",
    placeholder: "gsk_...",
    background: "#F4ECEC80",
  },
  // {
  //   id: "grok",
  //   label: "Grok",
  //   description: "X.ai Grok 3, Grok 4 and more",
  //   icon: "https://dlvkyia8i4zmz.cloudfront.net/5p8NzQLS3KdleTBwHJBg_svgviewer_output__3_.svg",
  //   placeholder: "xai-...",
  //   background: "#00000010",
  // },
  {
    id: "openrouter",
    label: "OpenRouter",
    description: "Access any model via OpenRouter",
    icon: "https://dlvkyia8i4zmz.cloudfront.net/b4nHxmER7jWwIQWXlzZg_svgviewer_output__6_.svg",
    placeholder: "sk-or-v1-...",
    background: "#F0F0F080",
  },
];

// Models grouped by provider — mirrors AiBotData.ts
interface ProviderModel {
  name: string;
  displayName: string;
}
const normalize = (s: string) =>
  s
    .toLowerCase()
    .replace(/^(gpt-|claude-|gemini-)/, "")
    .replace(/[\s-]/g, "");

const normalizeProviderKey = (provider?: string | null) => {
  const value = (provider || "").toLowerCase().trim();
  const compactValue = value.replace(/[\s_-]/g, "");
  if (!value) return "";
  if (value.includes("moonshot")) return "moonshotai";
  if (value.includes("z-ai") || value.includes("z.ai") || value.includes("glm"))
    return "z-ai";
  if (value.includes("custom-model")) return CUSTOM_MODEL_PROVIDER_ID;
  if (value.includes("openrouter") || value.includes("open router"))
    return "openrouter";
  if (value.includes("google") || value.includes("gemini")) return "google";
  if (value.includes("minimax")) return "minimax";
  if (value.includes("openai") || value.includes("gpt")) return "openai";
  if (value.includes("anthropic") || value.includes("claude"))
    return "anthropic";
  if (value.includes("deepseek")) return "deepseek";
  if (value.includes("groq")) return "groq";
  if (value.includes("grok")) return "grok";
  if (value.includes("auto")) return "auto";
  return value;
};

const inferProviderFromModelName = (modelName?: string | null) => {
  const value = (modelName || "").toLowerCase().trim();
  if (!value) return "";
  if (value.startsWith("auto")) return "auto";
  if (
    value.includes("moonshotai/kimi-k2.6") ||
    value.includes("moonshotai/kimi-k2.5") ||
    value.includes("moonshotai/kimi-k3") ||
    value.includes("kimi-k2.6") ||
    value.includes("kimi-k3") ||
    value.includes("moonshot")
  ) {
    return "moonshotai";
  }
  if (value.includes("gemini") || value.includes("google")) return "google";
  if (value.includes("minimax")) return "minimax";
  if (value.includes("gpt") || value.includes("openai")) return "openai";
  if (value.includes("claude") || value.includes("anthropic"))
    return "anthropic";
  if (value.includes("deepseek")) return "deepseek";
  if (value.includes("groq") || value.includes("kimi") || value.includes("oss"))
    return "groq";
  if (value.includes("grok")) return "grok";

  for (const [provider, providerModels] of Object.entries(PROVIDER_MODELS)) {
    if (
      providerModels.some(
        (model) =>
          model.name.toLowerCase() === value ||
          model.displayName.toLowerCase() === value,
      )
    ) {
      return provider;
    }
  }

  return "";
};

const PROVIDER_MODELS: Record<string, ProviderModel[]> = {
  auto: [
    { name: "auto-low-cost", displayName: "Low Cost" },
    { name: "auto-balanced", displayName: "Balanced" },
    { name: "auto-quality", displayName: "Quality" },
  ],
  openai: [
    // { name: "gpt-5.6-sol", displayName: "OpenAI GPT 5.6 Sol" },
    { name: "gpt-5.6-terra", displayName: "OpenAI GPT 5.6 Terra" },
    { name: "gpt-5.6-luna", displayName: "OpenAI GPT 5.6 Luna" },
    { name: "gpt-5.5", displayName: "OpenAI GPT 5.5" },
    { name: "gpt-5.4", displayName: "OpenAI GPT 5.4 (Recommended)" },
    { name: "gpt-5.4-mini", displayName: "OpenAI GPT 5.4 Mini" },
    { name: "gpt-5.4-nano", displayName: "OpenAI GPT 5.4 Nano" },
    { name: "gpt-5", displayName: "OpenAI GPT 5" },
    { name: "gpt-5-mini", displayName: "OpenAI GPT 5 Mini" },
    { name: "4.1", displayName: "OpenAI GPT 4.1" },
    { name: "4.1-mini", displayName: "OpenAI GPT 4.1 Mini" },
    { name: "4o", displayName: "OpenAI GPT-4o" },
    { name: "4o-mini", displayName: "OpenAI GPT-4o Mini" },
    { name: "4.1-nano", displayName: "OpenAI GPT 4.1 Nano" },
  ],
  anthropic: [
    // {
    //   name: "4.7-opus",
    //   displayName: "Anthropic Claude Opus 4.7",
    // },
    // {
    //   name: "4.8-opus",
    //   displayName: "Anthropic Claude Opus 4.8",
    // },
    {
      name: "5-sonnet",
      displayName: "Claude 5 Sonnet (Recommended)",
    },
    { name: "4.6-sonnet", displayName: "Claude 4.6 Sonnet" },
    { name: "4.5-sonnet", displayName: "Claude 4.5 Sonnet" },
    { name: "4.5-haiku", displayName: "Claude 4.5 Haiku" },
    // {
    //   name: "4-sonnet",
    //   displayName: "Claude 4 Sonnet (Recommended)",
    // },
    // { name: "4-opus", displayName: "Claude 4 Opus" },
  ],
  google: [
    // {
    //   name: "Gemini 3.1 Pro Preview",
    //   displayName: "Google Gemini 3.1 Pro Preview",
    // },
    // {
    //   name: "Gemini 3.1 Flash Lite Preview",
    //   displayName: "Google Gemini 3.1 Flash Lite Preview",
    // },
    // {
    //   name: "Gemini 3 Flash Preview",
    //   displayName: "Google Gemini 3 Flash Preview",
    // },
    { name: "Gemini 3.6 Flash", displayName: "Google Gemini 3.6 Flash" },
    {
      name: "Gemini 3.5 Flash Lite",
      displayName: "Google Gemini 3.5 Flash Lite",
    },
    { name: "Gemini 2.5 Pro", displayName: "Google Gemini 2.5 Pro" },
    { name: "Gemini 2.5 Flash", displayName: "Google Gemini 2.5 Flash" },
    {
      name: "Gemini 2.5 Flash Lite",
      displayName: "Google Gemini 2.5 Flash Lite",
    },
  ],
  grok: [
    {
      name: "x-ai/grok-4.3",
      displayName: "X Grok 4.3 (Recommended)",
    },
  ],
  groq: [
    // { name: "kimi-k2.5", displayName: "Moonshot Kimi K2.5" },
    // { name: "kimi-k2", displayName: "Moonshot Kimi K2 (Recommended)" },
    { name: "oss-120b", displayName: "OpenAI OSS 120B (Recommended)" },
  ],
  minimax: [
    { name: "minimax/minimax-m2.5", displayName: "MiniMax M2.5" },
    {
      name: "minimax/minimax-m2.7",
      displayName: "MiniMax M2.7 (Recommended)",
    },
  ],
  "z-ai": [
    { name: "glm-5.2", displayName: "Z.ai GLM 5.2 (Recommended)" },
    // { name: "glm-5.1", displayName: "Z.ai GLM 5.1" },
    // { name: "glm-5-turbo", displayName: "Z.ai GLM 5 Turbo" },
    // { name: "glm-4.7", displayName: "Z.ai GLM 4.7" },
    // { name: "glm-4.6", displayName: "Z.ai GLM 4.6" },
    // { name: "glm-4.5", displayName: "Z.ai GLM 4.5" },
  ],
  moonshotai: [
    {
      name: "moonshotai/kimi-k3",
      displayName: "Moonshot Kimi K3",
    },
    {
      name: "moonshotai/kimi-k2.6",
      displayName: "Moonshot Kimi K2.6",
    },
    {
      name: "moonshotai/kimi-k2.5",
      displayName: "Moonshot Kimi K2.5 (Recommended)",
    },
    {
      name: "x-ai/grok-4.3",
      displayName: "X Grok 4.3 (Recommended)",
    },
  ],
  deepseek: [
    { name: "deepseek-v4-pro", displayName: "DeepSeek V4 Pro" },
    { name: "deepseek-v4-flash", displayName: "DeepSeek V4 Flash" },
    { name: "deepseek-chat", displayName: "DeepSeek Chat V3" },
  ],
  openrouter: [
    { name: "auto-low-cost", displayName: "Auto Low Cost" },
    { name: "auto-balanced", displayName: "Auto Balanced" },
    { name: "auto-quality", displayName: "Auto Quality" },
    // {
    //   name: "4.7-opus",
    //   displayName: "Anthropic Claude Opus 4.7",
    // },
    {
      name: "5-sonnet",
      displayName: "Anthropic Claude 5 Sonnet",
    },
    { name: "4.6-sonnet", displayName: "Anthropic Claude 4.6 Sonnet" },
    { name: "4.5-sonnet", displayName: "Anthropic Claude 4.5 Sonnet" },
    { name: "4.5-haiku", displayName: "Anthropic Claude 4.5 Haiku" },
    // { name: "4-sonnet", displayName: "Anthropic Claude 4 Sonnet" },
    // { name: "gpt-5.6-sol", displayName: "OpenAI GPT 5.6 Sol" },
    { name: "gpt-5.6-terra", displayName: "OpenAI GPT 5.6 Terra" },
    { name: "gpt-5.6-luna", displayName: "OpenAI GPT 5.6 Luna" },
    { name: "gpt-5.5", displayName: "OpenAI GPT 5.5" },
    { name: "gpt-5.4", displayName: "OpenAI GPT 5.4 (Recommended)" },

    { name: "gpt-5.4-mini", displayName: "OpenAI GPT 5.4 Mini" },
    { name: "gpt-5.4-nano", displayName: "OpenAI GPT 5.4 Nano" },
    { name: "gpt-5", displayName: "OpenAI GPT 5" },
    { name: "gpt-5-mini", displayName: "OpenAI GPT 5 Mini" },
    { name: "4.1", displayName: "OpenAI GPT 4.1" },
    { name: "4o", displayName: "OpenAI GPT 4o" },
    { name: "4.1-nano", displayName: "OpenAI GPT 4.1 Nano" },
    { name: "4o-mini", displayName: "OpenAI GPT 4o Mini" },
    { name: "4.1-mini", displayName: "OpenAI GPT 4.1 mini" },
    // {
    //   name: "Gemini 3.1 Pro Preview",
    //   displayName: "Google Gemini 3.1 Pro Preview",
    // },
    // {
    //   name: "Gemini 3 Flash Preview",
    //   displayName: "Google Gemini 3 Flash Preview",
    // },
    // {
    //   name: "Gemini 3.1 Flash Lite Preview",
    //   displayName: "Google Gemini 3.1 Flash Lite Preview",
    // },
    { name: "Gemini 3.6 Flash", displayName: "Google Gemini 3.6 Flash" },
    {
      name: "Gemini 3.5 Flash Lite",
      displayName: "Google Gemini 3.5 Flash Lite",
    },
    { name: "Gemini 2.5 Pro", displayName: "Google Gemini 2.5 Pro" },
    { name: "Gemini 2.5 Flash", displayName: "Google Gemini 2.5 Flash" },
    {
      name: "Gemini 2.5 Flash Lite",
      displayName: "Google Gemini 2.5 Flash Lite",
    },
    { name: "minimax/minimax-m2.5", displayName: "MiniMax M2.5" },
    {
      name: "minimax/minimax-m2.7",
      displayName: "MiniMax M2.7 (Recommended)",
    },
    {
      name: "moonshotai/kimi-k3",
      displayName: "Moonshot Kimi K3",
    },
    {
      name: "moonshotai/kimi-k2.6",
      displayName: "Moonshot Kimi K2.6",
    },
    {
      name: "moonshotai/kimi-k2.5",
      displayName: "Moonshot Kimi K2.5 (Recommended)",
    },
    {
      name: "glm-5.2",
      displayName: "Z.ai GLM 5.2 (Recommended)",
    },
    // { name: "glm-5.1", displayName: "Z.ai GLM 5.1" },
    // { name: "glm-5-turbo", displayName: "Z.ai GLM 5 Turbo" },
    // { name: "glm-4.7", displayName: "Z.ai GLM 4.7" },
    // { name: "glm-4.6", displayName: "Z.ai GLM 4.6" },
    // { name: "glm-4.5", displayName: "Z.ai GLM 4.5" },
    {
      name: "x-ai/grok-4.3",
      displayName: "X Grok 4.3 (Recommended)",
    },
    { name: "oss-120b", displayName: "OpenAI GPT-OSS 120B" },
    // { name: "deepseek-r1-0528", displayName: "DeepSeek R1 (0528)" },
    { name: "deepseek-v4-pro", displayName: "DeepSeek V4 Pro" },
    { name: "deepseek-v4-flash", displayName: "DeepSeek V4 Flash" },
    { name: "deepseek-chat", displayName: "DeepSeek Chat V3" },
  ],
};

const getRecommendedModelForProvider = (provider: string) => {
  const providerKey = normalizeProviderKey(provider);
  let models = PROVIDER_MODELS[providerKey] || [];
  if (providerKey === "openrouter") {
    models = models.filter((m) => !m.name.toLowerCase().includes("auto"));
  }

  // 1. Priority: Models explicitly marked as (Recommended)
  const recommended = models.find((m) =>
    m.displayName.toLowerCase().includes("(recommended)"),
  );
  if (recommended) return recommended.name;

  // 2. Next Priority: small models (mini, haiku, flash-lite, flash, nano)
  const smallModel = models.find(
    (m) =>
      m.name.toLowerCase().includes("mini") ||
      m.name.toLowerCase().includes("haiku") ||
      m.name.toLowerCase().includes("flash-lite") ||
      m.name.toLowerCase().includes("flash") ||
      m.name.toLowerCase().includes("nano") ||
      m.name.toLowerCase().includes("low-cost"),
  );
  return smallModel ? smallModel.name : models[0]?.name || "";
};

const isOpenAICompatibleProvider = (provider?: string | null) =>
  normalizeProviderKey(provider) === CUSTOM_MODEL_PROVIDER_ID;

const providerIconById = (id: string) =>
  PROVIDERS.find((p) => p.id === id)?.icon?.trim() || "";

// Wire protocol of a custom model endpoint, shown to users as a "provider
// type"; ids must match the backend's CUSTOM_MODEL_API_FORMATS. Only the
// OpenAI-compatible type has no official default URL.
const DEFAULT_CUSTOM_MODEL_API_FORMAT = "openai-chat";
const CUSTOM_MODEL_API_FORMATS = [
  {
    id: "openai-chat",
    label: "Most providers (OpenAI-compatible)",
    description:
      "OpenAI, OpenRouter, Groq, DeepSeek, Mistral, Together, Ollama and similar",
    icon: providerIconById("auto"),
    baseUrlPlaceholder: "https://api.openai.com/v1",
    baseUrlRequired: true,
  },
  {
    id: "anthropic",
    label: "Anthropic Claude",
    description: "Claude models with an Anthropic API key",
    icon: providerIconById("anthropic"),
    baseUrlPlaceholder: "https://api.anthropic.com/v1",
    baseUrlRequired: false,
  },
  {
    id: "google",
    label: "Google Gemini",
    description: "Gemini models with a Google AI Studio API key",
    icon: providerIconById("google"),
    baseUrlPlaceholder: "https://generativelanguage.googleapis.com/v1beta",
    baseUrlRequired: false,
  },
  {
    id: "openai-responses",
    label: "OpenAI Responses API",
    description: "Only if your provider asks for the Responses API",
    icon: providerIconById("openai"),
    baseUrlPlaceholder: "https://api.openai.com/v1",
    baseUrlRequired: false,
  },
];

const getCustomModelApiFormat = (apiFormat?: string | null) =>
  CUSTOM_MODEL_API_FORMATS.find((f) => f.id === apiFormat) ||
  CUSTOM_MODEL_API_FORMATS[0];

const getProviderDisplayName = (provider?: string | null) =>
  normalizeProviderKey(provider) === CUSTOM_MODEL_PROVIDER_ID
    ? "Custom Model"
    : PROVIDERS.find((p) => p.id === normalizeProviderKey(provider))?.label ||
      provider ||
      "Unknown";

const getBestModelForProvider = (
  provider: string,
  preferredModel?: string | null,
) => {
  const providerKey = normalizeProviderKey(provider);
  const models = PROVIDER_MODELS[providerKey] || [];
  if (models.length === 0) return "";

  const candidate = (preferredModel || "").trim();
  if (!candidate) {
    return getRecommendedModelForProvider(providerKey);
  }

  const exactMatch = models.find((m) => m.name === candidate);
  if (exactMatch) return exactMatch.name;

  const normalizedCandidate = normalizeModelId(candidate);
  const normalizedMatch = models.find(
    (m) =>
      normalizeModelId(m.name) === normalizedCandidate ||
      normalizeModelId(m.displayName) === normalizedCandidate,
  );
  if (normalizedMatch) return normalizedMatch.name;

  return getRecommendedModelForProvider(providerKey);
};

const getModelForConfigSelection = (
  config: { provider?: string; model?: string },
  preferredModel?: string | null,
) => {
  if (isOpenAICompatibleProvider(config.provider)) {
    return (config.model || preferredModel || "").trim();
  }

  return getBestModelForProvider(config.provider || "", preferredModel);
};

const getProviderModelsForConfig = (
  config?: { provider?: string; model?: string; models?: string[] } | null,
  providerFallback?: string | null,
  modelFallback?: string | null,
): ProviderModel[] => {
  const providerKey = normalizeProviderKey(
    config?.provider || providerFallback,
  );
  if (providerKey === "custom-model") {
    const savedModels =
      Array.isArray(config?.models) && config!.models.length > 0
        ? config!.models
        : typeof config?.model === "string" && config.model.trim()
          ? [config.model.trim()]
          : [];

    if (savedModels.length === 0 && modelFallback?.trim()) {
      savedModels.push(modelFallback.trim());
    }

    return savedModels.map((modelName) => ({
      name: modelName,
      displayName: modelName,
    }));
  }

  return PROVIDER_MODELS[providerKey] || [];
};

const getAutoModeSelection = (botMode?: string) => {
  if (botMode === "lowCost") return "auto-low-cost";
  if (botMode === "quality") return "auto-quality";
  return "auto-balanced";
};

const normalizeModelId = (s: string) =>
  s
    .toLowerCase()
    // 1. Strip provider prefix (openai/, etc.)
    .replace(
      /^(openai\/|anthropic\/|google\/|groq\/|deepseek\/|grok\/|openrouter\/|z-ai\/)/,
      "",
    )
    // 2. Strip anything before first colon or slash (namespace/provider)
    .replace(/^[a-z0-9_-]+[\/:]/, "")
    // 3. Strip everything inside parentheses (e.g., "(Recommended)")
    .replace(/\(.*?\)/g, "")
    // 4. Strip model series prefixes and common noise keywords
    .replace(/^(gpt-|claude-|gemini-)/, "")
    .replace(/^(gpt|claude|gemini)[\s-_]*/, "")
    .replace(
      /(recommended|fast|mini|preview|lite|pro|sonnet|opus|haiku|flash|nano|maverick|v\d+)/gi,
      "",
    )
    // 5. Strip brand names and fluff
    .replace(
      /^(openai|anthropic|google|gemini|groq|deepseek|grok|openrouter|meta|mistral|microsoft|cohere|moonshot|llama)[\s-_]*/,
      "",
    )
    .replace(/[\s-]/g, "");

/* ─── Types ────────────────────────────────────────────────────────────── */
interface MaestroFlowConfig {
  selectedMode?: "lowCost" | "balanced" | "quality" | "custom";
}

interface Bot {
  _id: string;
  projectName: string;
  botCustomId: string;
  botModel?: string;
  aiConfig?: {
    model?: string;
  };
  maestroFlowConfig?: MaestroFlowConfig;
  aiBotConfig?: {
    configId?: string;
    model?: string;
    byokModel?: string; // isolated BYOK model (always the last BYOK selection)
    platformModel?: string; // isolated platform model (always the last platform selection)
    promptCacheKey?: string;
    maestroFlowConfig?: MaestroFlowConfig;
  };
  aiSource?: {
    youtube?: any[];
    confluence?: any[];
    urls?: any[];
    googlepicker?: any[];
    notion?: any[];
    salesforce?: any[];
    documents?: any[];
    textArea?: Record<string, any>;
  };
}

interface BotMap {
  [botCustomId: string]: { configId: string; provider: string; model: string };
}

interface SourceConfig {
  _id: string;
  name?: string;
  provider?: string;
  providerMode?: string;
  baseUrl?: string;
  apiFormat?: string;
  apiKey?: string;
  maskedKey?: string;
  model?: string;
}

interface Props {
  onClose: () => void;
  onBackToDashboard?: () => void | Promise<void>;
  /** Called after successful BYOK setup */
  onSuccess?: () => void | Promise<void>;
  /** When provided (delete-key flow), only these bots are shown in Step 2 */
  initialBots?: Bot[];
  /** Operating mode of the modal */
  mode?: "onboarding" | "add" | "edit";
  /** Edit mode config ID (alias for configId) */
  editConfigId?: string;
  /** Edit mode provider (alias for provider) */
  editConfigProvider?: string;
  /** New config ID (used in AI Bot Tab) */
  configId?: string;
  /** New provider (used in AI Bot Tab) */
  provider?: string;
  /** User ID (optional) */
  userId?: string;
  /** Inline mode (optional) */
  inline?: boolean;
  /** Bot IDs that have a model whose provider has no matching BYOK key */
  impactedBotIds?: Set<string>;
  /** Explicit BYOK state from parent when available */
  isByokEnabled?: boolean;
  /** Sidebar "Add API key" and similar flows — success toast should not say "BYOK" */
  suppressByokSuccessToast?: boolean;
  sourceConfig?: SourceConfig;
  /** Hide floating dismiss — use only for builder gate (dashboard “Add API key” keeps the X) */
  hideCloseButton?: boolean;
  /** Show dashboard escape hatch for builder-gated legacy AppSumo users */
  showBackToDashboardButton?: boolean;
}

/* ─── Component ─────────────────────────────────────────────────────────── */
const ByokOnboarding = ({
  onClose,
  onBackToDashboard,
  onSuccess,
  initialBots,
  mode = "onboarding",
  editConfigId = "",
  editConfigProvider = "",
  configId = "",
  provider = "",
  userId = "",
  inline = false,
  impactedBotIds,
  suppressByokSuccessToast = false,
  sourceConfig,
  hideCloseButton = false,
  showBackToDashboardButton = false,
}: Props) => {
  const { data: selectedCompany, refetch: refetchCompany } =
    useGetSingleCompany();
  const finalConfigId = editConfigId || configId;
  const finalProvider = editConfigProvider || provider;
  const dispatch = useDispatch();
  const { data: userDetails } = useUserDetails();
  const isNewUser = useSelector((state: any) => state.user.isNewUser);
  const byok = selectedCompany?.byok ?? false;
  const plan = {
    ...useSelector((state: any) => state.user.plan),
    ...selectedCompany?.ai_subscription?.plan,
  };
  const allowedModels = plan?.ai_currentLimits?.allowedModels;
  const isBasicOrProOrTrialPlan = plan?.name?.toLowerCase?.() !== "lifetime";

  const isLegacyAppSumoUser = plan?.name === "Lifetime" && !isNewUser;
  const getInitialBotCustomId = (bot: Bot | string | undefined | null) =>
    typeof bot === "string" ? bot : bot?.botCustomId || "";
  const isInlineSingleBotFlow =
    inline &&
    Array.isArray(initialBots) &&
    initialBots.length === 1 &&
    !!getInitialBotCustomId(initialBots[0] as Bot | string | undefined);
  const shouldUseAiProviderSetupTitle =
    isLegacyAppSumoUser ||
    isBasicOrProOrTrialPlan ||
    (byok && isInlineSingleBotFlow);
  const usesCustomAiSetupLabel =
    byok && !isBasicOrProOrTrialPlan && !isLegacyAppSumoUser;
  const onboardingTitle = byok
    ? shouldUseAiProviderSetupTitle
      ? "AI Provider Setup"
      : "BYOK Setup"
    : "AI Model Setup";
  const isAddingModelToExistingKey = mode === "add" && !!sourceConfig?._id;

  /* ── State ── */
  const [step, setStep] = useState<1 | 2>(impactedBotIds?.size ? 2 : 1);
  const [selectedProvider, setSelectedProvider] =
    useState<string>(finalProvider);
  const [providerBeforeCustomModel, setProviderBeforeCustomModel] =
    useState<string>(finalProvider);
  const [apiKey, setApiKey] = useState<string>("");
  const [configName, setConfigName] = useState<string>("");
  const [baseUrl, setBaseUrl] = useState<string>("");
  const [apiFormat, setApiFormat] = useState<string>(
    DEFAULT_CUSTOM_MODEL_API_FORMAT,
  );
  const [apiFormatMenuOpen, setApiFormatMenuOpen] = useState(false);
  const [customModelId, setCustomModelId] = useState<string>("");
  const [existingConfigs, setExistingConfigs] = useState<any[]>([]);
  const [keyVisible, setKeyVisible] = useState<boolean>(false);
  const [canDismissBuilderGate, setCanDismissBuilderGate] = useState(false);
  const [isReturningToDashboard, setIsReturningToDashboard] = useState(false);

  const [bots, setBots] = useState<Bot[]>([]);
  const [botMap, setBotMap] = useState<BotMap>({});
  const [savedConfigId, setSavedConfigId] = useState<string>(finalConfigId);

  const [loading, setLoading] = useState(false);
  const [botsLoading, setBotsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [botSearch, setBotSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [sortOrder, setSortOrder] = useState<"recent" | "name">("recent");
  const [selectedBots, setSelectedBots] = useState<Set<string>>(new Set());
  const [openDropdownBotId, setOpenDropdownBotId] = useState<string | null>(
    null,
  );
  const [openDropdownType, setOpenDropdownType] = useState<
    "config" | "model" | "bulk-config" | "bulk-model" | null
  >(null);
  const [dropdownPos, setDropdownPos] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const modalRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const filterMenuRef = useRef<HTMLDivElement>(null);
  const [filterDropdownPos, setFilterDropdownPos] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [modelFilter, setModelFilter] = useState<string>("");
  const [openModelFilter, setOpenModelFilter] = useState(false);
  const [dropdownSearch, setDropdownSearch] = useState("");
  const shouldSkipBotSelectionForSingleBot =
    isBasicOrProOrTrialPlan ||
    (byok && isInlineSingleBotFlow && mode !== "edit");
  const isCustomModelFormOpen = isOpenAICompatibleProvider(selectedProvider);
  const selectedApiFormat = getCustomModelApiFormat(apiFormat);

  /* Close dropdowns on outside click */
  useEffect(() => {
    if (
      !openDropdownBotId &&
      !openModelFilter &&
      openDropdownType !== "bulk-config" &&
      openDropdownType !== "bulk-model"
    )
      return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        openDropdownBotId &&
        dropdownMenuRef.current &&
        !dropdownMenuRef.current.contains(target)
      ) {
        setOpenDropdownBotId(null);
        setOpenDropdownType(null);
      }
      if (
        (openModelFilter ||
          openDropdownType === "bulk-config" ||
          openDropdownType === "bulk-model") &&
        filterMenuRef.current &&
        !filterMenuRef.current.contains(target)
      ) {
        setOpenModelFilter(false);
        if (
          openDropdownType === "bulk-config" ||
          openDropdownType === "bulk-model"
        ) {
          setOpenDropdownType(null);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdownBotId, openModelFilter, openDropdownType]);

  useEffect(() => {
    if (!openDropdownBotId) return;
    const scrollContainer = document.querySelector(".byok-table-body");
    if (!scrollContainer) return;
    const handleScroll = () => {
      setOpenDropdownBotId(null);
      setOpenDropdownType(null);
    };
    scrollContainer.addEventListener("scroll", handleScroll);
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [openDropdownBotId]);

  useEffect(() => {
    setDropdownSearch("");
  }, [openDropdownBotId, openDropdownType]);

  // Reposition dropdown if it overflows the viewport
  useEffect(() => {
    const menuEl = dropdownMenuRef.current || filterMenuRef.current;
    if (!menuEl) return;
    requestAnimationFrame(() => {
      const rect = menuEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      if (rect.bottom > viewportHeight - 8) {
        // Move dropdown up so it fits within the viewport
        const overflow = rect.bottom - viewportHeight + 8;
        const newTop = rect.top - overflow;
        menuEl.style.top = `${Math.max(8, newTop)}px`;
      }
      if (rect.top < 8) {
        menuEl.style.top = "8px";
      }
    });
  }, [openDropdownBotId, openDropdownType, dropdownPos, filterDropdownPos]);

  /* ── Helpers ── */
  const toast = (type: "success" | "error", message: string) =>
    dispatch(
      userSlice.actions.setToastPopUp({ show: true, toastType: type, message }),
    );

  const handleOpenCustomModelForm = () => {
    if (!isOpenAICompatibleProvider(selectedProvider)) {
      setProviderBeforeCustomModel(selectedProvider);
    }
    setSelectedProvider(CUSTOM_MODEL_PROVIDER_ID);
    setConfigName((prev) => prev || "Custom Model Key");
  };

  const handleBackFromCustomModelForm = () => {
    setSelectedProvider(providerBeforeCustomModel || "");
  };

  /* ── Fetch existing AI configs on mount ── */
  const fetchConfigs = useCallback(async () => {
    try {
      const res = await axiosGet("/configurations/allAIConfigs");
      const configs = res.data?.data ?? [];
      setExistingConfigs(configs);
      return configs;
    } catch {
      return [];
    }
  }, []);

  /* ── When provider is selected, reset inputs ── */
  useEffect(() => {
    if (!selectedProvider) return;
    if (isAddingModelToExistingKey) return;

    const defaultName =
      selectedProvider === CUSTOM_MODEL_PROVIDER_ID
        ? "Custom Model Key"
        : PROVIDERS.find((p) => p.id === selectedProvider)?.label + " Key" ||
          "";
    setConfigName((prev) => prev || defaultName);
    if (mode !== "edit") {
      setApiKey("");
      setBaseUrl("");
      setApiFormat(DEFAULT_CUSTOM_MODEL_API_FORMAT);
      setCustomModelId("");
    }
  }, [selectedProvider, isAddingModelToExistingKey, mode]);

  useEffect(() => {
    if (!isAddingModelToExistingKey || !sourceConfig) return;

    const providerKey =
      normalizeProviderKey(sourceConfig.provider || finalProvider) ||
      sourceConfig.provider ||
      finalProvider ||
      "";
    setSelectedProvider(providerKey);
    setConfigName("");
    setApiKey("");
    setBaseUrl(sourceConfig.baseUrl || "");
    setApiFormat(sourceConfig.apiFormat || DEFAULT_CUSTOM_MODEL_API_FORMAT);
    setCustomModelId("");
    setSavedConfigId("");
  }, [isAddingModelToExistingKey, sourceConfig, finalProvider]);

  /* ── Fetch bots ── */
  const fetchBots = useCallback(
    async (
      configs: any[],
      newSavedConfigId?: string,
      forceProvider?: string,
      currentMap?: BotMap,
    ) => {
      const configIdToUse = newSavedConfigId || savedConfigId;
      const providerToUse = normalizeProviderKey(
        forceProvider || selectedProvider,
      );

      const hasValidInitialBots =
        Array.isArray(initialBots) &&
        initialBots.length > 0 &&
        initialBots.every(
          (bot) =>
            !!bot &&
            typeof bot === "object" &&
            typeof bot.botCustomId === "string",
        );

      if (hasValidInitialBots && mode !== "onboarding") {
        const providerModels = PROVIDER_MODELS[providerToUse] ?? [];
        const configToUse =
          configs.find((config: any) => config._id === configIdToUse) ||
          sourceConfig ||
          null;
        const modelsForConfig = getProviderModelsForConfig(
          configToUse,
          providerToUse,
        );
        const initial: BotMap = {};
        initialBots.forEach((bot) => {
          const currentModel =
            byok || configIdToUse
              ? bot.botModel ||
                bot.aiBotConfig?.model ||
                bot.aiConfig?.model ||
                ""
              : bot.botModel ||
                bot.aiConfig?.model ||
                bot.aiBotConfig?.model ||
                "";
          const botMode =
            bot.aiBotConfig?.maestroFlowConfig?.selectedMode ||
            bot.maestroFlowConfig?.selectedMode ||
            "balanced";
          const isAutoModel =
            currentModel === "auto" || currentModel.startsWith("auto-");
          const matchedProviderModel = providerModels.find(
            (m) => m.name === currentModel,
          );
          const matchedConfigModel = modelsForConfig.find(
            (m) => m.name === currentModel,
          );
          initial[bot.botCustomId] = {
            configId: configIdToUse,
            provider: providerToUse,
            model:
              isAutoModel && providerToUse === "openrouter"
                ? getAutoModeSelection(botMode)
                : matchedConfigModel
                  ? matchedConfigModel.name
                  : matchedProviderModel
                    ? currentModel
                    : modelsForConfig[0]?.name || providerModels[0]?.name || "",
          };
        });
        setBots(initialBots);

        setBotMap(initial);
        return;
      }

      setBotsLoading(true);
      try {
        const res = await axiosGet(
          "/users/allBotInOne?status=&sort=&page=&per_page",
        );
        const fetchedBots: Bot[] = res.data?.data?.bots || [];
        setBots(fetchedBots);
        const initial: BotMap = currentMap ? { ...currentMap } : {};

        const newConfig = configs.find((c) => c._id === configIdToUse);

        fetchedBots.forEach((bot) => {
          // 0. Priority: Preserve manual changes in this session
          const existingMapping = initial[bot.botCustomId];
          const isValidInInitial =
            existingMapping?.configId &&
            configs.some((c: any) => c._id === existingMapping.configId);

          if (isValidInInitial) {
            return;
          }

          let currentConfigId = bot.aiBotConfig?.configId;
          let currentModelForRender = "";

          const platformModelForMapping =
            byok || currentConfigId
              ? bot.botModel ||
                bot.aiBotConfig?.model ||
                bot.aiConfig?.model ||
                ""
              : bot.botModel ||
                bot.aiConfig?.model ||
                bot.aiBotConfig?.model ||
                "";

          const isAuto =
            platformModelForMapping === "auto" ||
            platformModelForMapping.startsWith("auto-");
          const botMode =
            bot.aiBotConfig?.maestroFlowConfig?.selectedMode ||
            bot.maestroFlowConfig?.selectedMode ||
            "balanced";
          const autoModeSelection = getAutoModeSelection(botMode);

          let deducedPlatformModel = platformModelForMapping;
          if (isAuto) {
            deducedPlatformModel = getAutoModeModelName(botMode);
          }

          const normalized = normalizeModelId(deducedPlatformModel);
          let modelProvider: string | null = null;
          let directConfig: any = null;

          if (
            deducedPlatformModel &&
            !deducedPlatformModel.startsWith("auto")
          ) {
            const lowerPlatform = deducedPlatformModel.toLowerCase();
            if (
              lowerPlatform.includes("gpt") ||
              lowerPlatform.includes("o1") ||
              lowerPlatform.includes("o3")
            ) {
              modelProvider = "openai";
            } else {
              for (const [pId, pModels] of Object.entries(PROVIDER_MODELS)) {
                if (pId === "openrouter" || pId === "auto") continue;
                const match = pModels.some(
                  (m) =>
                    normalizeModelId(m.name) === normalized ||
                    normalizeModelId(m.displayName) === normalized,
                );
                if (match) {
                  modelProvider = pId;
                  break;
                }
              }
            }
          }

          if (modelProvider) {
            directConfig = configs.find(
              (c: any) => c.provider === modelProvider,
            );
          }

          let resolvedConfig: any = null;

          if (isAuto) {
            const openRouterConfig = configs.find(
              (c: any) => c.provider === "openrouter",
            );
            if (openRouterConfig) {
              resolvedConfig = openRouterConfig;
              currentModelForRender = autoModeSelection;
            }
          }

          /* ─── PRIORITY 2: Exact Backend Match ─── */
          const existingBackendConfig = resolvedConfig
            ? null
            : configs.find((c: any) => c._id === currentConfigId);

          if (existingBackendConfig) {
            resolvedConfig = existingBackendConfig;
            currentModelForRender =
              bot.botModel || bot.aiBotConfig?.model || "";
          } else if (directConfig) {
            /* ─── PRIORITY 3: Direct Provider match (Model Analysis) ─── */
            resolvedConfig = directConfig;
          } else if (
            /* ─── PRIORITY 4: New Key Match (Contextual addition) ─── */
            newConfig &&
            modelProvider === providerToUse &&
            providerToUse !== "openrouter"
          ) {
            resolvedConfig = newConfig;
          } else if (
            /* ─── PRIORITY 5: Newly added key (Contextual Context) ─── */
            newConfig &&
            (mode === "add" || mode === "onboarding") &&
            (!!impactedBotIds?.has(bot.botCustomId) || !currentConfigId)
          ) {
            resolvedConfig = newConfig;
          } else {
            /* ─── PRIORITY 6: OpenRouter Fallback ─── */
            const orConfig = configs.find(
              (c: any) => c.provider === "openrouter",
            );
            if (orConfig) resolvedConfig = orConfig;
            else if (configs.length > 0) resolvedConfig = configs[0];
          }

          if (resolvedConfig) {
            const preferredModelForResolvedConfig =
              currentModelForRender ||
              platformModelForMapping ||
              deducedPlatformModel;
            currentModelForRender = getModelForConfigSelection(
              resolvedConfig,
              preferredModelForResolvedConfig,
            );
          }

          // Final UI adjustment for auto models
          if (currentModelForRender === "auto") {
            currentModelForRender = autoModeSelection;
          }

          if (resolvedConfig) {
            initial[bot.botCustomId] = {
              configId: resolvedConfig._id,
              provider: resolvedConfig.provider,
              model: currentModelForRender,
            };
          }
        });

        setBotMap(initial);

        if (byok) {
          const botUpdates = fetchedBots.map((bot) => ({
            botCustomId: bot.botCustomId,
            model: initial[bot.botCustomId].model,
            configId: initial[bot.botCustomId].configId,
            timestamps: false,
            maestroFlowConfig: bot.aiBotConfig?.maestroFlowConfig || {},
          }));

          await axiosPut("/users/updateByokModels", { botUpdates });
        }
      } catch (err) {
        console.error("fetchBots error:", err);
        toast("error", "Failed to load agents. Please try again.");
      } finally {
        setBotsLoading(false);
      }
    },
    [
      selectedProvider,
      initialBots,
      savedConfigId,
      mode,
      impactedBotIds,
      byok,
      sourceConfig,
    ],
  );

  useEffect(() => {
    let mounted = true;
    if (
      (mode === "edit" ||
        (step === 2 &&
          (mode === "add" ||
            (mode === "onboarding" && impactedBotIds?.size)))) &&
      Object.keys(botMap).length === 0
    ) {
      fetchConfigs().then((configs) => {
        if (mounted) fetchBots(configs, undefined, undefined, botMap);
      });
    } else {
      fetchConfigs();
    }
    return () => {
      mounted = false;
    };
  }, [mode, step, fetchConfigs, fetchBots]);

  useEffect(() => {
    if (mode !== "edit" || !finalConfigId || existingConfigs.length === 0) {
      return;
    }

    const configToEdit = existingConfigs.find(
      (cfg) => cfg._id === finalConfigId,
    );
    if (!configToEdit) return;

    setSelectedProvider(configToEdit.provider || finalProvider || "");
    setConfigName(configToEdit.name || "");
    setApiKey(configToEdit.apiKey || "");
    setBaseUrl(configToEdit.baseUrl || "");
    setApiFormat(configToEdit.apiFormat || DEFAULT_CUSTOM_MODEL_API_FORMAT);
    setCustomModelId(configToEdit.model || "");
    setSavedConfigId(configToEdit._id || finalConfigId);
  }, [mode, finalConfigId, existingConfigs, finalProvider]);

  /* ── Step 1 → Step 2 ── */
  const handleNext = async () => {
    setCanDismissBuilderGate(false);
    const isOpenAICompatible = isOpenAICompatibleProvider(selectedProvider);
    const usesSourceConfig = isAddingModelToExistingKey && !!sourceConfig?._id;

    if (!selectedProvider) {
      toast("error", "Please select an AI provider.");
      return;
    }

    if (!usesSourceConfig && !apiKey.trim()) {
      toast("error", "Please enter your API key.");
      return;
    }

    if (
      isOpenAICompatible &&
      selectedApiFormat.baseUrlRequired &&
      !baseUrl.trim() &&
      !sourceConfig?.baseUrl
    ) {
      toast("error", "Please enter the provider base URL.");
      return;
    }

    if (isOpenAICompatible && !customModelId.trim()) {
      toast("error", "Please enter the model ID.");
      return;
    }

    setLoading(true);
    try {
      const providerDisplayName =
        PROVIDERS.find((p) => p.id === selectedProvider)?.label ||
        selectedProvider;
      const defaultModel = isOpenAICompatible
        ? customModelId.trim()
        : getRecommendedModelForProvider(selectedProvider);
      const payloadName =
        configName.trim() ||
        (usesSourceConfig
          ? `${sourceConfig?.name || providerDisplayName} - ${defaultModel}`
          : `${providerDisplayName} Key`);
      const payload = {
        provider: sourceConfig?.provider || selectedProvider,
        providerMode:
          sourceConfig?.providerMode ||
          (isOpenAICompatible ? "custom-model" : "native"),
        baseUrl: isOpenAICompatible
          ? baseUrl.trim() || sourceConfig?.baseUrl
          : undefined,
        apiFormat: isOpenAICompatible ? selectedApiFormat.id : undefined,
        apiKey: usesSourceConfig ? undefined : apiKey.trim(),
        name: payloadName,
        model: defaultModel,
      };

      const res =
        mode === "edit" && finalConfigId
          ? await axiosPut(
              `/configurations/updateAIConfig?configId=${finalConfigId}`,
              payload,
            )
          : usesSourceConfig && sourceConfig?._id
            ? await axiosPatch(
                `/configurations/updateAIConfig?configId=${sourceConfig._id}`,
                {
                  model: defaultModel,
                  name: sourceConfig.name,
                  addModel: true,
                },
              )
            : await axiosPost("/configurations/addAIConfig", payload);
      const newConfigId =
        res.data?.data?._id ||
        res.data?.data?.configId ||
        res.data?.updatedData?._id ||
        res.data?.updatedData?.configId ||
        res.data?.saveConfig?._id ||
        res.data?.saveConfig?.configId ||
        (usesSourceConfig ? sourceConfig?._id : undefined) ||
        finalConfigId ||
        "";
      setSavedConfigId(newConfigId);

      const refreshed = await fetchConfigs();
      const saved =
        refreshed.find((c: any) => c._id === newConfigId) ||
        refreshed[refreshed.length - 1];
      if (saved) setSavedConfigId(saved._id);
      setCanDismissBuilderGate(true);

      if (mode === "edit") {
        toast("success", "AI provider updated successfully.");
        await onSuccess?.();
        onClose();
        return;
      }

      if (isAddingModelToExistingKey) {
        toast("success", "Model added successfully to the existing key.");
        await onSuccess?.();
        onClose();
        return;
      }

      if (isLegacyAppSumoUser) {
        const effectiveConfigId = saved?._id || newConfigId;
        const botUpdates = isInlineSingleBotFlow
          ? getSingleBotRecommendedUpdates(
              effectiveConfigId,
              selectedProvider,
              defaultModel,
            )
          : [];
        await completeSetup(botUpdates, effectiveConfigId, false);
        return;
      }

      if (shouldSkipBotSelectionForSingleBot && !isNewUser) {
        const effectiveConfigId = saved?._id || newConfigId;
        const botUpdates = getSingleBotRecommendedUpdates(
          effectiveConfigId,
          selectedProvider,
          defaultModel,
        );

        await completeSetup(botUpdates, effectiveConfigId, false);
        return;
      }

      setStep(2);
      fetchBots(refreshed, saved?._id || newConfigId, selectedProvider, botMap);
    } catch (err: any) {
      console.error("BYOK Save Error:", err);
      // Surface validation error from backend
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to save API key. Please try again.";
      if (
        typeof errMsg === "string" &&
        /already exists|already added|already configured|existing configuration/i.test(
          errMsg,
        )
      ) {
        setCanDismissBuilderGate(true);
      }
      toast("error", errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToDashboard = async () => {
    if (!onBackToDashboard || isReturningToDashboard) return;
    setIsReturningToDashboard(true);
    try {
      await onBackToDashboard();
    } finally {
      setIsReturningToDashboard(false);
    }
  };

  const getSingleBotRecommendedUpdates = useCallback(
    (effectiveConfigId: string, providerId: string, modelOverride?: string) => {
      const recommendedModel =
        modelOverride || getRecommendedModelForProvider(providerId);
      const selectedMode =
        recommendedModel === "auto-low-cost"
          ? "lowCost"
          : recommendedModel === "auto-balanced"
            ? "balanced"
            : recommendedModel === "auto-quality"
              ? "quality"
              : undefined;

      return (initialBots || [])
        .map((bot) => getInitialBotCustomId(bot as Bot | string | undefined))
        .filter(Boolean)
        .map((botCustomId) => ({
          botCustomId,
          model: recommendedModel.startsWith("auto-")
            ? "auto"
            : recommendedModel,
          configId: effectiveConfigId,
          timestamps: false,
          maestroFlowConfig: selectedMode ? { selectedMode } : undefined,
        }));
    },
    [initialBots],
  );

  const completeSetup = useCallback(
    async (
      botUpdates: any[],
      effectiveConfigId: string,
      shouldMapExistingBots = true,
    ) => {
      if ((mode === "onboarding" || mode === "add") && effectiveConfigId) {
        await axiosPut("/users/updateByok", {
          byok: true,
          defaultByokConfigId: effectiveConfigId,
          shouldMapExistingBots,
        });
      }

      if (botUpdates.length > 0) {
        await axiosPut("/users/updateByokModels", { botUpdates });
      }

      const useByokSuccessCopy =
        usesCustomAiSetupLabel &&
        !suppressByokSuccessToast &&
        userDetails?.appSumo !== true;
      toast(
        "success",
        useByokSuccessCopy
          ? "BYOK setup complete! Your agents are now using your custom AI provider."
          : "Setup complete! Your agents are now using your custom AI provider.",
      );
      await onSuccess?.();
      onClose();
    },
    [
      mode,
      onClose,
      onSuccess,
      suppressByokSuccessToast,
      userDetails?.appSumo,
      usesCustomAiSetupLabel,
    ],
  );

  /* ── Finish: save per-bot models + enable BYOK ── */
  const handleFinish = async () => {
    const unmappedImpactedBots = bots.filter(
      (bot) =>
        !!impactedBotIds?.has(bot.botCustomId) &&
        !botMap[bot.botCustomId]?.configId,
    );
    if (unmappedImpactedBots.length > 0) {
      toast("error", "You need to first map the affected agents.");
      return;
    }

    setSaving(true);
    try {
      const botUpdates = bots
        .map((bot) => {
          const mapping = botMap[bot.botCustomId];
          if (!mapping) return null;

          const isAuto = mapping.model.startsWith("auto-");
          let selectedMode: any = undefined;
          if (mapping.model === "auto-low-cost") selectedMode = "lowCost";
          else if (mapping.model === "auto-balanced") selectedMode = "balanced";
          else if (mapping.model === "auto-quality") selectedMode = "quality";

          return {
            botCustomId: bot.botCustomId,
            model: isAuto ? "auto" : mapping.model,
            configId: mapping.configId,
            timestamps: false,
            maestroFlowConfig: isAuto ? { selectedMode } : undefined,
          };
        })
        .filter(Boolean);

      const effectiveConfigId =
        savedConfigId ||
        Object.values(botMap).find((m) => m.configId)?.configId ||
        "";
      await completeSetup(botUpdates, effectiveConfigId);
    } catch (err: any) {
      toast(
        "error",
        err.response?.data?.message ??
          "Setup failed. Please check your configuration.",
      );
    } finally {
      setSaving(false);
    }
  };

  const providerModels = PROVIDER_MODELS[selectedProvider] ?? [];
  const canProceedStep1 =
    !!selectedProvider &&
    (isAddingModelToExistingKey || apiKey.trim().length > 0) &&
    (!isOpenAICompatibleProvider(selectedProvider) ||
      ((!selectedApiFormat.baseUrlRequired ||
        baseUrl.trim().length > 0 ||
        !!sourceConfig?.baseUrl) &&
        customModelId.trim().length > 0));
  /* ─── Step 2 derived data ─────────────────────────────────────────────── */
  const currentProvider = PROVIDERS.find((p) => p.id === selectedProvider);
  const filteredBots = bots.filter((bot) =>
    bot.projectName?.toLowerCase().includes(botSearch.toLowerCase()),
  );
  const sortedBots = [...filteredBots].sort((a, b) => {
    if (sortOrder === "name")
      return (a.projectName || "").localeCompare(b.projectName || "");
    return 0;
  });
  const allSelected =
    sortedBots.length > 0 &&
    sortedBots.every((b) => selectedBots.has(b.botCustomId));

  // Count configs and models across selected agents
  const configCounts: Record<string, number> = {};
  const modelCounts: Record<string, number> = {};
  const providerCounts: Record<string, number> = {};

  if (selectedBots.size > 0) {
    selectedBots.forEach((botId) => {
      const mapped = botMap[botId];
      if (mapped) {
        configCounts[mapped.configId] =
          (configCounts[mapped.configId] || 0) + 1;
        modelCounts[mapped.model] = (modelCounts[mapped.model] || 0) + 1;
        providerCounts[mapped.provider] =
          (providerCounts[mapped.provider] || 0) + 1;
      }
    });
  }

  const distinctConfigs = Object.keys(configCounts);
  const distinctModels = Object.keys(modelCounts);
  const distinctProviders = Object.keys(providerCounts);

  // Find the majority (most common) config
  const majorityConfigId =
    distinctConfigs.length > 0
      ? distinctConfigs.reduce((a, b) =>
          configCounts[a] >= configCounts[b] ? a : b,
        )
      : null;

  // Find the majority (most common) model
  const majorityModel =
    distinctModels.length > 0
      ? distinctModels.reduce((a, b) =>
          modelCounts[a] >= modelCounts[b] ? a : b,
        )
      : null;

  const majorityProvider =
    distinctProviders.length > 0
      ? distinctProviders.reduce((a, b) =>
          providerCounts[a] >= providerCounts[b] ? a : b,
        )
      : null;

  const bulkSelectionMixedConfigOrProvider =
    selectedBots.size >= 2 &&
    (distinctProviders.length > 1 || distinctConfigs.length > 1);

  const bulkModelMixedModels =
    selectedBots.size >= 2 && distinctModels.length > 1;

  let bulkConfigLabel = "Select provider";
  if (selectedBots.size > 0 && !bulkSelectionMixedConfigOrProvider) {
    if (majorityConfigId) {
      const cfg = existingConfigs.find((c) => c._id === majorityConfigId);
      bulkConfigLabel = cfg?.name || "Select config";
    } else if (majorityProvider) {
      bulkConfigLabel =
        PROVIDERS.find((p) => p.id === majorityProvider)?.label ||
        majorityProvider;
    }
  }

  // Mixed providers disables bulk model dropdown; mixed models needs explicit choice
  const bulkModelMixedProviders = (() => {
    if (selectedBots.size < 2) return false;
    let first: string | null = null;
    for (const botId of selectedBots) {
      const provider = botMap[botId]?.provider;
      if (!first) first = provider;
      else if (first !== provider) return true;
    }
    return false;
  })();

  let bulkModelLabel = "Change model";
  if (bulkModelMixedProviders || bulkModelMixedModels) {
    bulkModelLabel = "Select model";
  } else if (selectedBots.size > 0 && majorityModel) {
    let modelDisplayName = majorityModel;
    for (const cfg of existingConfigs) {
      const pModels = getProviderModelsForConfig(
        cfg,
        cfg.provider,
        majorityModel,
      );
      const m = pModels.find((x) => x.name === majorityModel);
      if (m) {
        modelDisplayName = m.displayName;
        break;
      }
    }
    bulkModelLabel = modelDisplayName;
  }

  /* ─── Render ─────────────────────────────────────────────────────────── */
  return createPortal(
    <div className="createbot-modal" onClick={(e) => e.stopPropagation()}>
      <div
        ref={modalRef}
        className="byok-modal-wrapper flex max-h-[95dvh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`createbot-container byok-onboarding-container ${
            step === 2 ? "!w-[820px]" : ""
          } ${isCustomModelFormOpen ? "byok-custom-model-container" : ""}`}
        >
          <div className="byok-onb-header">
            {isCustomModelFormOpen ? (
              <div className="byok-custom-model-modal-heading">
                <h2>Add Custom AI Model</h2>
                <p>
                  Connect any model through an OpenAI, Anthropic or Gemini
                  compatible API.
                </p>
              </div>
            ) : (
              <div className="byok-onb-logo">
                <div className="byok-logo-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="19"
                    height="19"
                    viewBox="0 0 19 19"
                    fill="none"
                  >
                    <path
                      d="M15.3841 2.79874C14.4816 1.89499 13.3009 1.32167 12.0326 1.1713C10.7643 1.02093 9.48226 1.30229 8.39346 1.96996C7.30466 2.63764 6.47257 3.6527 6.03147 4.85133C5.59037 6.04996 5.56598 7.36225 5.96225 8.57644L2.04038 12.4983C1.9344 12.6035 1.85037 12.7286 1.79319 12.8665C1.73601 13.0044 1.7068 13.1523 1.70728 13.3016V15.3392C1.70728 15.6406 1.827 15.9296 2.04011 16.1428C2.25322 16.3559 2.54226 16.4756 2.84364 16.4756H5.11637C5.26706 16.4756 5.41158 16.4157 5.51814 16.3092C5.62469 16.2026 5.68455 16.0581 5.68455 15.9074V14.771H6.82092C6.97161 14.771 7.11613 14.7112 7.22268 14.6046C7.32924 14.4981 7.3891 14.3536 7.3891 14.2029V13.0665H8.52546C8.6001 13.0666 8.67402 13.0519 8.74299 13.0234C8.81197 12.9949 8.87465 12.9531 8.92745 12.9003L9.60643 12.2206C10.1747 12.4055 10.7687 12.4992 11.3664 12.4983H11.3735C12.4967 12.497 13.5942 12.1627 14.5276 11.5379C15.4609 10.913 16.1881 10.0255 16.6172 8.98759C17.0464 7.94964 17.1583 6.80777 16.9389 5.70623C16.7194 4.6047 16.1784 3.59293 15.3841 2.79874ZM12.7868 6.53241C12.5621 6.53241 12.3424 6.46576 12.1555 6.3409C11.9686 6.21603 11.823 6.03856 11.737 5.83091C11.651 5.62327 11.6285 5.39479 11.6723 5.17435C11.7161 4.95392 11.8244 4.75144 11.9833 4.59252C12.1422 4.43359 12.3447 4.32536 12.5651 4.28152C12.7856 4.23767 13.0141 4.26017 13.2217 4.34618C13.4293 4.43219 13.6068 4.57784 13.7317 4.76472C13.8565 4.95159 13.9232 5.17129 13.9232 5.39605C13.9232 5.69743 13.8035 5.98647 13.5904 6.19958C13.3772 6.41269 13.0882 6.53241 12.7868 6.53241Z"
                      fill="url(#paint0_linear_7076_90822)"
                    />
                    <defs>
                      <linearGradient
                        id="paint0_linear_7076_90822"
                        x1="1.70728"
                        y1="8.80368"
                        x2="17.0484"
                        y2="8.80368"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="#FF787E" />
                        <stop offset="1" stopColor="#E3232C" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <div className="grid">
                  <span className="byok-logo-title">{onboardingTitle}</span>
                  <span className="byok-logo-subtitle">
                    {byok
                      ? step === 1
                        ? "Choose the AI provider you want to use for your agents."
                        : "Choose the AI provider and model each agent should use."
                      : "Configure platform AI models for your agents."}
                  </span>
                </div>
              </div>
            )}

            {mode !== "edit" &&
              !isCustomModelFormOpen &&
              !isLegacyAppSumoUser &&
              !shouldSkipBotSelectionForSingleBot && (
                <div className="byok-step-indicators">
                  <div className="byok-step-dots">
                    <div
                      className={`byok-step-dot ${step === 1 ? "byok-step-dot--active" : "byok-step-dot--done"}`}
                    />
                    <div
                      className={`byok-step-dot ${step >= 2 ? "byok-step-dot--active" : "byok-step-dot--inactive"}`}
                    />
                  </div>
                  <span className="byok-step-counter">{step}/2</span>
                </div>
              )}
          </div>

          {/* ── Body ── */}
          <div className="byok-onb-body">
            {/* ━━━━━━━━━━━━ STEP 1 ━━━━━━━━━━━━ */}
            {step === 1 && (
              <div className="byok-step-content">
                {!isCustomModelFormOpen && (
                  <>
                    <div className="byok-step1-header flex items-center justify-between gap-[15px]">
                      <h2 className="byok-step1-title">
                        Select Your AI Provider
                      </h2>
                      <button
                        type="button"
                        className="byok-custom-model-cta"
                        onClick={handleOpenCustomModelForm}
                      >
                        <GradientPlusIcon /> Add Custom Model
                      </button>
                    </div>

                    {/* Provider grid */}
                    <div className="byok-provider-grid">
                      {PROVIDERS.filter(
                        (p) => !["auto", "deepseek"].includes(p.id),
                      ).map((provider) => (
                        <div
                          key={provider.id}
                          className={`byok-provider-card ${selectedProvider === provider.id ? "byok-provider-selected" : ""}`}
                          onClick={() => {
                            if (!isAddingModelToExistingKey) {
                              setSelectedProvider(provider.id);
                            }
                          }}
                          style={{ background: provider.background }}
                        >
                          <div className="byok-provider-icon-wrap">
                            <img
                              src={provider.icon}
                              alt={provider.label}
                              className="byok-provider-icon"
                            />
                          </div>
                          <span className="byok-provider-name">
                            {provider.label}
                          </span>
                          <div
                            className={`byok-provider-radio ${selectedProvider === provider.id ? "byok-provider-radio--active" : ""}`}
                          >
                            {selectedProvider === provider.id && (
                              <div className="byok-provider-radio-dot" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Provider Credentials */}
                {selectedProvider && (
                  <div
                    className={
                      isCustomModelFormOpen ? "byok-custom-model-panel" : ""
                    }
                  >
                    {!isCustomModelFormOpen && (
                      <div className="mb-[12px] mt-[20px]">
                        <h3 className="font-medium text-[#444]">
                          Provider Credentials
                        </h3>
                      </div>
                    )}
                    <div className="byok-key-section">
                      {!isAddingModelToExistingKey && (
                        <div className="byok-key-field">
                          <label className="byok-label">
                            Configuration Name
                          </label>
                          <input
                            type="text"
                            className="byok-input"
                            value={configName}
                            onChange={(e) => setConfigName(e.target.value)}
                            placeholder={
                              isCustomModelFormOpen
                                ? "e.g. Production OpenAI"
                                : "e.g. OpenAI Production Key"
                            }
                          />
                        </div>
                      )}
                      {isOpenAICompatibleProvider(selectedProvider) && (
                        <div className="byok-key-field">
                          <label className="byok-label">
                            Provider Type
                            <span className="byok-required">*</span>
                          </label>
                          <div
                            className="byok-format-select"
                            onKeyDown={(e) => {
                              if (e.key === "Escape")
                                setApiFormatMenuOpen(false);
                            }}
                          >
                            <button
                              type="button"
                              className={`byok-input byok-format-trigger ${apiFormatMenuOpen ? "byok-format-trigger--open" : ""}`}
                              onClick={() => setApiFormatMenuOpen((v) => !v)}
                              disabled={isAddingModelToExistingKey}
                              aria-haspopup="listbox"
                              aria-expanded={apiFormatMenuOpen}
                            >
                              {selectedApiFormat.icon && (
                                <img
                                  src={selectedApiFormat.icon}
                                  alt=""
                                  className="byok-dropdown-icon"
                                />
                              )}
                              <span className="byok-format-trigger-label">
                                {selectedApiFormat.label}
                              </span>
                              <svg
                                className={`byok-dropdown-chevron ${apiFormatMenuOpen ? "byok-dropdown-chevron--open" : ""}`}
                                width="10"
                                height="6"
                                viewBox="0 0 10 6"
                                fill="none"
                              >
                                <path
                                  d="M1 1l4 4 4-4"
                                  stroke="#888"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                            {apiFormatMenuOpen && (
                              <>
                                <div
                                  className="byok-dropdown-backdrop"
                                  onClick={() => setApiFormatMenuOpen(false)}
                                />
                                <div
                                  className="byok-dropdown-menu byok-format-menu"
                                  role="listbox"
                                >
                                  {CUSTOM_MODEL_API_FORMATS.map((format) => {
                                    const isSelected =
                                      format.id === selectedApiFormat.id;
                                    return (
                                      <button
                                        type="button"
                                        key={format.id}
                                        role="option"
                                        aria-selected={isSelected}
                                        className={`byok-dropdown-option byok-format-option ${isSelected ? "byok-dropdown-option--selected" : ""}`}
                                        onClick={() => {
                                          setApiFormat(format.id);
                                          setApiFormatMenuOpen(false);
                                        }}
                                      >
                                        {format.icon && (
                                          <img
                                            src={format.icon}
                                            alt=""
                                            className="byok-dropdown-option-icon"
                                          />
                                        )}
                                        <span className="byok-format-option-text">
                                          <span className="byok-format-option-title">
                                            {format.label}
                                          </span>
                                          <span className="byok-format-option-desc">
                                            {format.description}
                                          </span>
                                        </span>
                                        {isSelected && (
                                          <svg
                                            className="byok-dropdown-option-check"
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="#FB5F66"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          >
                                            <path d="M20 6 9 17l-5-5" />
                                          </svg>
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              </>
                            )}
                          </div>
                          <div className="byok-key-hint">
                            Not sure? Keep &quot;Most providers&quot;. It works
                            with almost every service.
                          </div>
                        </div>
                      )}
                      {isOpenAICompatibleProvider(selectedProvider) && (
                        <div className="byok-key-field">
                          <label className="byok-label">
                            Base URL
                            {selectedApiFormat.baseUrlRequired && (
                              <span className="byok-required">*</span>
                            )}
                          </label>
                          <input
                            type="text"
                            className="byok-input"
                            value={baseUrl}
                            onChange={(e) => setBaseUrl(e.target.value)}
                            disabled={isAddingModelToExistingKey}
                            placeholder={selectedApiFormat.baseUrlPlaceholder}
                          />
                          {!selectedApiFormat.baseUrlRequired && (
                            <div className="byok-key-hint">
                              Leave empty to use the official endpoint.
                            </div>
                          )}
                        </div>
                      )}
                      {isAddingModelToExistingKey ? (
                        <div className="mb-[15px]">
                          <label className="byok-label">API Key</label>
                          <input
                            type="text"
                            className="byok-input"
                            value={
                              sourceConfig?.maskedKey || "Using existing key"
                            }
                            disabled
                          />
                          <div className="byok-key-hint">
                            The new model will use this existing key.
                          </div>
                        </div>
                      ) : (
                        <div className="mb-[15px]">
                          <label className="byok-label">
                            API Key
                            <span className="byok-required">*</span>
                          </label>
                          <div className="byok-key-input-wrapper">
                            <input
                              type={keyVisible ? "text" : "password"}
                              className="byok-input byok-input--with-icon"
                              value={apiKey}
                              onChange={(e) => setApiKey(e.target.value)}
                              placeholder={
                                isCustomModelFormOpen
                                  ? "Paste your provider API key"
                                  : "Enter your provider API key"
                              }
                            />
                            <button
                              type="button"
                              className="byok-eye-btn"
                              onClick={() => setKeyVisible((v) => !v)}
                              title={keyVisible ? "Hide key" : "Show key"}
                            >
                              {keyVisible ? (
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="#aaa"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                  <line x1="1" y1="1" x2="23" y2="23" />
                                </svg>
                              ) : (
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="#aaa"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                  <circle cx="12" cy="12" r="3" />
                                </svg>
                              )}
                            </button>
                          </div>
                          <div className="byok-key-hint">
                            Your key is encrypted and stored securely. It is
                            never shared.
                          </div>
                        </div>
                      )}
                      {isOpenAICompatibleProvider(selectedProvider) && (
                        <div className="byok-key-field">
                          <label className="byok-label">
                            Model ID
                            <span className="byok-required">*</span>
                          </label>
                          <input
                            type="text"
                            className="byok-input"
                            value={customModelId}
                            onChange={(e) => setCustomModelId(e.target.value)}
                            placeholder={
                              isCustomModelFormOpen
                                ? "e.g. gpt-5"
                                : "e.g. gpt-4.1-mini or provider/model-name"
                            }
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ━━━━━━━━━━━━ STEP 2 ━━━━━━━━━━━━ */}
            {step === 2 && (
              <div className="byok-step-content byok-step2-layout">
                {/* Impact warning banner */}
                {impactedBotIds && impactedBotIds.size > 0 && (
                  <div
                    className="mb-[12px] flex items-start gap-[10px] rounded-[10px] px-[14px] py-[10px] text-[12px] leading-[1.5]"
                    style={{
                      fontSize: "1px !important",
                      background: "#FFF0EF",
                      border: "1px solid #FBD0CE",
                      color: "#C0392B",
                    }}
                  >
                    <svg
                      className="mt-[1px] flex-shrink-0"
                      width="14"
                      height="14"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M8 1.5a6.5 6.5 0 1 0 0 13A6.5 6.5 0 0 0 8 1.5ZM0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8Z"
                        fill="#E03B2D"
                      />
                      <path
                        d="M7.25 4.75a.75.75 0 0 1 1.5 0v3.5a.75.75 0 0 1-1.5 0v-3.5ZM8 11.25a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"
                        fill="#E03B2D"
                      />
                    </svg>
                    <span className="text-[12px]">
                      <strong>
                        {impactedBotIds.size} agent
                        {impactedBotIds.size > 1 ? "s" : ""}
                      </strong>{" "}
                      currently use a provider you don't have a BYOK key for.{" "}
                      Use the <strong>provider dropdown</strong> on highlighted
                      rows to switch them to a key you own, then click{" "}
                      <strong>Finish and Save</strong>.
                    </span>
                  </div>
                )}

                {/* Search & filter bar */}
                <div className="byok-toolbar">
                  <div
                    className={`byok-search-wrapper ${searchOpen ? "byok-search-open" : ""}`}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="byok-search-icon"
                      onClick={() => {
                        setSearchOpen(true);
                        setTimeout(() => searchInputRef.current?.focus(), 200);
                      }}
                    >
                      <circle
                        cx="11"
                        cy="11"
                        r="7"
                        stroke="#bbb"
                        strokeWidth="2"
                      />
                      <path
                        d="M20 20L16.65 16.65"
                        stroke="#bbb"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search agents..."
                      value={botSearch}
                      onChange={(e) => setBotSearch(e.target.value)}
                      onBlur={() => {
                        if (!botSearch) setSearchOpen(false);
                      }}
                      className="byok-search-input"
                    />
                  </div>
                  {selectedBots.size > 0 && (
                    <div className="byok-filter-dropdown-container flex w-full items-center gap-[10px]">
                      {/* Bulk Config Dropdown */}
                      <>
                        <div
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            if (openDropdownType === "bulk-config") {
                              setOpenDropdownType(null);
                            } else {
                              const rect =
                                e.currentTarget.getBoundingClientRect();
                              setFilterDropdownPos({
                                top: rect.bottom + 4,
                                left: rect.left,
                                width: rect.width < 200 ? 200 : rect.width,
                              });
                              setOpenDropdownType("bulk-config");
                              setOpenModelFilter(false);
                            }
                          }}
                          className={`byok-filter-trigger hiiiiii relative ${openDropdownType === "bulk-config" ? "byok-filter-trigger--open" : ""}`}
                        >
                          <span
                            className="byok-filter-trigger-label"
                            // style={{
                            //   overflow: "hidden",
                            //   whiteSpace: "nowrap",
                            //   textOverflow: "ellipsis",
                            // }}
                            onMouseEnter={(e) => {
                              const container = e.currentTarget;
                              const content = container.querySelector(
                                ".marquee-content",
                              ) as HTMLElement;
                              if (!content) return;
                              const overflow =
                                content.scrollWidth - container.clientWidth;
                              if (overflow > 0) {
                                const duration = overflow / 40;
                                content.style.transition = `transform ${duration}s linear`;
                                content.style.transform = `translateX(-${overflow}px)`;
                              }
                            }}
                            onMouseLeave={(e) => {
                              const content = e.currentTarget.querySelector(
                                ".marquee-content",
                              ) as HTMLElement;
                              if (!content) return;
                              content.style.transition = "transform 0.3s ease";
                              content.style.transform = "translateX(0)";
                            }}
                          >
                            <span
                              className="marquee-content text-[13px]"
                              style={{
                                display: "inline-block",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {bulkConfigLabel}
                            </span>
                          </span>
                          <svg
                            width="10"
                            height="6"
                            viewBox="0 0 11 6"
                            fill="none"
                            className={`byok-dropdown-chevron ${openDropdownType === "bulk-config" ? "byok-dropdown-chevron--open" : ""}`}
                          >
                            <path
                              d="M0.583008 0.291992L5.79134 5.70866L10.9997 0.291992L0.583008 0.291992Z"
                              fill="#6D6D6D"
                            />
                          </svg>
                        </div>
                        {openDropdownType === "bulk-config" &&
                          createPortal(
                            <div
                              ref={filterMenuRef}
                              className="byok-dropdown-menu"
                              style={{
                                position: "fixed",
                                top: filterDropdownPos.top,
                                left: filterDropdownPos.left,
                                width: 230,
                              }}
                            >
                              {/* <SearchBar
                                  value={dropdownSearch}
                                  onChange={setDropdownSearch}
                                  placeholder="Search configs..."
                                  wrapperClassName="byok-dropdown-search"
                                  autoFocus
                                  onClick={(e) => e.stopPropagation()}
                                /> */}
                              {dropdownSearch === "" && (
                                <>
                                  {!byok &&
                                    !savedConfigId &&
                                    mode !== "onboarding" &&
                                    mode !== "add" && (
                                      <div className="byok-dropdown-label-group px-3 py-1 text-[10px] font-bold uppercase text-gray-400">
                                        Platform Providers
                                      </div>
                                    )}
                                  {!byok &&
                                    !savedConfigId &&
                                    mode !== "onboarding" &&
                                    mode !== "add" &&
                                    PROVIDERS.filter(
                                      (p) => p.id !== "openrouter",
                                    ).map((p) => (
                                      <div
                                        key={`platform-${p.id}`}
                                        onClick={() => {
                                          const normalizedProvider =
                                            normalizeProviderKey(p.id);
                                          setBotMap((prev) => {
                                            const updated = { ...prev };
                                            selectedBots.forEach((botId) => {
                                              updated[botId] = {
                                                configId: "", // Clear config for platform
                                                provider: normalizedProvider,
                                                model: getBestModelForProvider(
                                                  normalizedProvider,
                                                  prev[botId]?.model,
                                                ),
                                              };
                                            });
                                            return updated;
                                          });
                                          setOpenDropdownType(null);
                                          setDropdownSearch("");
                                        }}
                                        className={`byok-dropdown-option ${majorityProvider === p.id ? "byok-dropdown-option--selected" : ""}`}
                                      >
                                        <img
                                          src={p.icon}
                                          alt=""
                                          className="byok-dropdown-icon mr-2 h-4 w-4"
                                        />
                                        <span className="byok-dropdown-option-label truncate">
                                          {p.label}
                                        </span>
                                      </div>
                                    ))}
                                  {(byok ||
                                    savedConfigId ||
                                    mode === "onboarding" ||
                                    mode === "add") && (
                                    <>
                                      <div className="byok-dropdown-label-group mt-1 px-3 py-1 text-[10px] font-bold uppercase text-gray-400">
                                        Your Custom Keys
                                      </div>
                                    </>
                                  )}
                                </>
                              )}
                              {(byok ||
                                savedConfigId ||
                                mode === "onboarding" ||
                                mode === "add") &&
                              existingConfigs.length === 0 ? (
                                <div className="nothing-found text-align-center flex h-full w-full items-center justify-center text-[12px] text-[#444]">
                                  No custom keys configured yet.
                                </div>
                              ) : (
                                (byok ||
                                  savedConfigId ||
                                  mode === "onboarding" ||
                                  mode === "add") &&
                                (() => {
                                  const filtered = existingConfigs.filter(
                                    (cfg) =>
                                      `${cfg.name} ${cfg.provider} ${cfg.model || ""} ${cfg.baseUrl || ""}`
                                        .toLowerCase()
                                        .includes(dropdownSearch.toLowerCase()),
                                  );
                                  if (filtered.length === 0 && dropdownSearch) {
                                    return (
                                      <div className="nothing-found text-align-center flex h-full w-full items-center justify-center text-[12px] text-[#444]">
                                        No matching configurations found
                                      </div>
                                    );
                                  }
                                  return filtered.map((cfg) => {
                                    return (
                                      <div
                                        key={cfg._id}
                                        onClick={() => {
                                          const newProvider =
                                            normalizeProviderKey(cfg.provider);
                                          setBotMap((prev) => {
                                            const updated = { ...prev };
                                            selectedBots.forEach((botId) => {
                                              const previousModel =
                                                prev[botId]?.model;
                                              updated[botId] = {
                                                configId: cfg._id,
                                                provider: newProvider,
                                                model:
                                                  getModelForConfigSelection(
                                                    cfg,
                                                    previousModel,
                                                  ),
                                              };
                                            });
                                            return updated;
                                          });
                                          setOpenDropdownType(null);
                                          setDropdownSearch("");
                                        }}
                                        className={`byok-dropdown-option ${majorityConfigId === cfg._id ? "byok-dropdown-option--selected" : ""}`}
                                      >
                                        <span className="byok-dropdown-option-label truncate">
                                          {cfg.name} (
                                          {getProviderDisplayName(cfg.provider)}
                                          )
                                        </span>
                                      </div>
                                    );
                                  });
                                })()
                              )}
                            </div>,
                            modalRef.current!,
                          )}
                      </>

                      {/* Bulk Model Dropdown */}
                      <div
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          if (bulkModelMixedProviders) return;
                          if (openDropdownType === "bulk-model") {
                            setOpenDropdownType(null);
                          } else {
                            const rect =
                              e.currentTarget.getBoundingClientRect();
                            setFilterDropdownPos({
                              top: rect.bottom + 4,
                              left: rect.left,
                              width: rect.width < 200 ? 200 : rect.width,
                            });
                            setOpenDropdownType("bulk-model");
                          }
                        }}
                        className={`byok-filter-trigger relative w-full ${openDropdownType === "bulk-model" ? "byok-filter-trigger--open" : ""} ${bulkModelMixedProviders ? "byok-filter-trigger--disabled" : ""}`}
                        title={
                          bulkModelMixedProviders
                            ? "Selected agents use different providers. Change Config first."
                            : undefined
                        }
                      >
                        <span
                          className="byok-filter-trigger-label"
                          // style={{
                          //   overflow: "hidden",
                          //   whiteSpace: "nowrap",
                          //   textOverflow: "ellipsis",
                          // }}
                          onMouseEnter={(e) => {
                            const container = e.currentTarget;
                            const content = container.querySelector(
                              ".marquee-content",
                            ) as HTMLElement;
                            if (!content) return;
                            const overflow =
                              content.scrollWidth - container.clientWidth;
                            if (overflow > 0) {
                              const duration = overflow / 40;
                              content.style.transition = `transform ${duration}s linear`;
                              content.style.transform = `translateX(-${overflow}px)`;
                            }
                          }}
                          onMouseLeave={(e) => {
                            const content = e.currentTarget.querySelector(
                              ".marquee-content",
                            ) as HTMLElement;
                            if (!content) return;
                            content.style.transition = "transform 0.3s ease";
                            content.style.transform = "translateX(0)";
                          }}
                        >
                          <span
                            className="marquee-content text-[13px]"
                            style={{
                              display: "inline-block",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {bulkModelLabel}
                          </span>
                        </span>
                        <svg
                          width="10"
                          height="6"
                          viewBox="0 0 11 6"
                          fill="none"
                          className={`byok-dropdown-chevron ${openDropdownType === "bulk-model" ? "byok-dropdown-chevron--open" : ""}`}
                        >
                          <path
                            d="M0.583008 0.291992L5.79134 5.70866L10.9997 0.291992L0.583008 0.291992Z"
                            fill="#6D6D6D"
                          />
                        </svg>
                      </div>
                      {openDropdownType === "bulk-model" &&
                        createPortal(
                          <div
                            ref={filterMenuRef}
                            className="byok-dropdown-menu"
                            style={{
                              position: "fixed",
                              top: filterDropdownPos.top,
                              left: filterDropdownPos.left,
                              width: 235,
                            }}
                          >
                            <SearchBar
                              value={dropdownSearch}
                              onChange={setDropdownSearch}
                              placeholder="Search models..."
                              wrapperClassName="byok-dropdown-search"
                              autoFocus
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div className="byok-dropdown-options-wrapper grey-scrollbar px-[5px]">
                              {(() => {
                                let sharedProvider: string | null = null;
                                let mixedProviders = false;

                                selectedBots.forEach((botId) => {
                                  const provider = botMap[botId]?.provider;
                                  if (!sharedProvider)
                                    sharedProvider = provider;
                                  else if (sharedProvider !== provider)
                                    mixedProviders = true;
                                });

                                if (mixedProviders) {
                                  return null;
                                }

                                const bulkProvider =
                                  normalizeProviderKey(
                                    sharedProvider || selectedProvider,
                                  ) ||
                                  inferProviderFromModelName(majorityModel);
                                const bulkConfig = majorityConfigId
                                  ? existingConfigs.find(
                                      (cfg) => cfg._id === majorityConfigId,
                                    )
                                  : null;
                                const modelsToShow = getProviderModelsForConfig(
                                  bulkConfig,
                                  bulkProvider,
                                  majorityModel,
                                );
                                if (modelsToShow.length === 0) {
                                  return (
                                    <div className="nothing-found text-align-center flex h-full w-full items-center justify-center text-[12px] text-[#444]">
                                      No models available.
                                    </div>
                                  );
                                }

                                const filtered = modelsToShow.filter((m) =>
                                  m.displayName
                                    .toLowerCase()
                                    .includes(dropdownSearch.toLowerCase()),
                                );
                                if (filtered.length === 0 && dropdownSearch) {
                                  return (
                                    <div className="nothing-found text-align-center flex h-full w-full items-center justify-center text-[12px] text-[#444]">
                                      No matching models found
                                    </div>
                                  );
                                }
                                return filtered.map((m) => {
                                  const isSelected = majorityModel === m.name;

                                  // Restriction logic
                                  const isAllowed =
                                    isSelected ||
                                    plan?.name === "Trial" ||
                                    plan?.name === "outgrow" ||
                                    plan?.name === "Lifetime" ||
                                    (m.name.startsWith("auto") &&
                                      (allowedModels?.includes("auto") ||
                                        allowedModels?.some(
                                          (am: string) =>
                                            normalize(am) === "auto",
                                        ))) ||
                                    allowedModels?.some((am: string) => {
                                      const nm = normalize(m.name);
                                      const na = normalize(am);
                                      return (
                                        nm === na ||
                                        m.name.toLowerCase() ===
                                          am.toLowerCase()
                                      );
                                    });

                                  const isDisabled = false; // !isAllowed;

                                  return (
                                    <div
                                      key={m.name}
                                      onClick={() => {
                                        if (isDisabled) return;
                                        setBotMap((prev) => {
                                          const updated = { ...prev };
                                          selectedBots.forEach((botId) => {
                                            if (
                                              updated[botId] &&
                                              sharedProvider
                                            ) {
                                              updated[botId] = {
                                                ...updated[botId],
                                                model: m.name,
                                              };
                                            }
                                          });
                                          return updated;
                                        });
                                        setOpenDropdownType(null);
                                        setDropdownSearch("");
                                      }}
                                      className={`byok-dropdown-option ${isSelected ? "byok-dropdown-option--selected" : ""} ${isDisabled ? "byok-dropdown-option--disabled" : ""}`}
                                    >
                                      <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
                                        <span
                                          className="byok-dropdown-option-label"
                                          onMouseEnter={(e) => {
                                            const container = e.currentTarget;
                                            const content =
                                              container.querySelector(
                                                ".marquee-content",
                                              ) as HTMLElement;
                                            if (!content) return;
                                            const overflow =
                                              content.scrollWidth -
                                              container.clientWidth;
                                            if (overflow > 0) {
                                              const duration = overflow / 40;
                                              content.style.transition = `transform ${duration}s linear`;
                                              content.style.transform = `translateX(-${overflow}px)`;
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            const content =
                                              e.currentTarget.querySelector(
                                                ".marquee-content",
                                              ) as HTMLElement;
                                            if (!content) return;
                                            content.style.transition =
                                              "transform 0.3s ease";
                                            content.style.transform =
                                              "translateX(0)";
                                          }}
                                        >
                                          <span className="marquee-content">
                                            {m.displayName}
                                          </span>
                                        </span>
                                      </div>
                                      {isDisabled && (
                                        <svg
                                          width="12"
                                          height="12"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          className="ml-auto text-gray-400"
                                        >
                                          <rect
                                            x="3"
                                            y="11"
                                            width="18"
                                            height="11"
                                            rx="2"
                                            ry="2"
                                          />
                                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                        </svg>
                                      )}
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          </div>,
                          modalRef.current!,
                        )}
                    </div>
                  )}
                  {/* <button
                    onClick={() =>
                      setSortOrder(sortOrder === "recent" ? "name" : "recent")
                    }
                    className="byok-sort-btn"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M3 6H21M6 12H18M9 18H15"
                        stroke="#888"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                    {sortOrder === "recent" ? "Recently Created" : "By Name"}
                  </button> */}
                  {/* <button className=" gap-[8px]  p-[10px] text-[13px] border rounded-[10px] border-gray-300 flex items-center bg-white text-[#444] relative" >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8 9.5C8.12744 9.50014 8.25002 9.54894 8.34268 9.63643C8.43535 9.72391 8.49112 9.84348 8.49859 9.9707C8.50605 10.0979 8.46466 10.2232 8.38287 10.3209C8.30107 10.4186 8.18505 10.4814 8.0585 10.4965L8 10.5H6C5.87256 10.4999 5.74998 10.4511 5.65732 10.3636C5.56465 10.2761 5.50888 10.1565 5.50141 10.0293C5.49395 9.90208 5.53534 9.77681 5.61713 9.67908C5.69893 9.58136 5.81495 9.51855 5.9415 9.5035L6 9.5H8ZM9.5 6.5C9.63261 6.5 9.75979 6.55268 9.85355 6.64645C9.94732 6.74022 10 6.86739 10 7C10 7.13261 9.94732 7.25979 9.85355 7.35355C9.75979 7.44732 9.63261 7.5 9.5 7.5H4.5C4.36739 7.5 4.24021 7.44732 4.14645 7.35355C4.05268 7.25979 4 7.13261 4 7C4 6.86739 4.05268 6.74022 4.14645 6.64645C4.24021 6.55268 4.36739 6.5 4.5 6.5H9.5ZM11 3.5C11.1326 3.5 11.2598 3.55268 11.3536 3.64645C11.4473 3.74021 11.5 3.86739 11.5 4C11.5 4.13261 11.4473 4.25979 11.3536 4.35355C11.2598 4.44732 11.1326 4.5 11 4.5H3C2.86739 4.5 2.74021 4.44732 2.64645 4.35355C2.55268 4.25979 2.5 4.13261 2.5 4C2.5 3.86739 2.55268 3.74021 2.64645 3.64645C2.74021 3.55268 2.86739 3.5 3 3.5H11Z"
                        fill="#666666"
                      />
                    </svg>
                    Filter

                    <div className="byok-filter-dropdown-container !absolute top-full right-0 mt-1 flex items-center gap-[10px] bg-white rounded-[10px] shadow-[0_4px_14px_0_rgba(0, 0, 0, 0.15)] w-[250px] z-index-[10] p-[15px] hidden">
                      hij
                      dgfbhnjk
                      sdfgbhnjd
                      dsfdgfbnkjlk
                      gbnj
                    </div>
                  </button> */}
                </div>

                {/* Agents table */}
                <div className="byok-table-wrapper">
                  {botsLoading ? (
                    <div className="byok-empty-state">
                      <div className="byok-loading-spinner" />
                      <div className="byok-loading-text">Loading agents...</div>
                    </div>
                  ) : sortedBots.length === 0 ? (
                    <div className="byok-empty-state">
                      <div className="byok-empty-text">
                        <NoDataOnboard />
                        {bots.length === 0
                          ? "No agents found. Create a agent first."
                          : "No agents match your search."}
                      </div>
                    </div>
                  ) : (
                    <div className="byok-table-body grey-scrollbar">
                      <table className="byok-table">
                        <thead>
                          <tr className="byok-table-header">
                            <th className="byok-table-col-check">
                              <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={() => {
                                  if (allSelected) {
                                    setSelectedBots(new Set());
                                  } else {
                                    setSelectedBots(
                                      new Set(
                                        sortedBots.map((b) => b.botCustomId),
                                      ),
                                    );
                                  }
                                }}
                                className="byok-checkbox"
                              />
                            </th>
                            <th className="byok-table-col-name flex items-center !justify-between">
                              Agent Name
                              <svg
                                className="cursor-pointer"
                                onClick={() =>
                                  setSortOrder(
                                    sortOrder === "recent" ? "name" : "recent",
                                  )
                                }
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M10.063 2.33331L10.0036 2.33731C9.79008 2.36628 9.62552 2.54932 9.62552 2.77081L9.625 10.1745L7.74672 8.29755L7.69767 8.2552C7.52634 8.12821 7.28327 8.14244 7.12798 8.29778C6.95724 8.46875 6.95736 8.74572 7.12827 8.91652L9.75549 11.5415L9.80455 11.5839C9.97587 11.7109 10.2189 11.6966 10.3742 11.5413L12.997 8.91629L13.0393 8.86717C13.1664 8.69585 13.1521 8.45277 12.9967 8.29755L12.9477 8.2552C12.7763 8.12821 12.5333 8.14244 12.378 8.29778L10.5 10.1768L10.5005 2.77081L10.4965 2.71145C10.4676 2.4979 10.2845 2.33331 10.063 2.33331ZM3.62507 2.46147L1.00313 5.08358L0.960774 5.13265C0.833695 5.30393 0.847823 5.54698 1.00315 5.7023L1.05222 5.74466C1.2235 5.87176 1.46655 5.85758 1.62187 5.70228L3.49825 3.82496L3.49864 11.232L3.50263 11.2914C3.5316 11.5049 3.71465 11.6695 3.93614 11.6695L3.9955 11.6655C4.20905 11.6365 4.37364 11.4535 4.37364 11.232L4.37325 3.82613L6.25327 5.70262L6.30239 5.74493C6.47377 5.87181 6.71679 5.85746 6.87196 5.70195C7.04264 5.53091 7.04235 5.2539 6.87132 5.08323L4.24346 2.46112L4.19439 2.41884C4.02311 2.29201 3.78027 2.30625 3.62507 2.46147Z"
                                  fill={
                                    sortOrder === "name" ? "#7C3AED" : "#666666"
                                  }
                                />
                              </svg>
                            </th>
                            <th className="byok-table-col-model">AI Model</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedBots.map((bot) => {
                            const botMapping = botMap[bot.botCustomId] || {};
                            const isModelDisabled =
                              mode !== "onboarding" &&
                              !botMapping.configId &&
                              !botMapping.provider;
                            const botConfig = existingConfigs.find(
                              (c) => c._id === botMapping.configId,
                            );

                            // Use the config's authoritative provider for the model list;
                            // this ensures OpenRouter configs show the full model list.
                            const resolvedProvider =
                              normalizeProviderKey(
                                botConfig?.provider || botMapping.provider,
                              ) || inferProviderFromModelName(botMapping.model);
                            const modelListProvider = resolvedProvider;

                            const pModels = getProviderModelsForConfig(
                              botConfig,
                              modelListProvider,
                              botMapping.model,
                            );
                            const selectedModel =
                              pModels.find(
                                (m) => m.name === botMapping.model,
                              ) ||
                              (botMapping.model
                                ? {
                                    name: botMapping.model,
                                    displayName: botMapping.model,
                                  }
                                : pModels[0]);
                            // Use the config's authoritative provider for the icon;
                            // botMapping.provider may be deduced from the model name (e.g. "google"
                            // for a Gemini model inside an OpenRouter config) which gives the wrong icon.
                            const iconProvider = resolvedProvider;
                            const pIcon =
                              PROVIDERS.find((p) => p.id === iconProvider)
                                ?.icon ||
                              (iconProvider === "moonshotai"
                                ? PROVIDERS.find((p) => p.id === "openrouter")
                                    ?.icon
                                : iconProvider === "z-ai"
                                  ? "https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/svg/z-ai.svg"
                                  : undefined);

                            let displayModelName = selectedModel?.displayName;
                            let displayIcon = pIcon;

                            if (
                              !displayModelName &&
                              botMapping.model &&
                              botMapping.configId
                            ) {
                              displayModelName = botMapping.model;
                              // Try to find a match in any provider list (robust lookup)
                              for (const [prov, models] of Object.entries(
                                PROVIDER_MODELS,
                              ) as [string, ProviderModel[]][]) {
                                const match = models.find(
                                  (m) =>
                                    m.name === botMapping.model ||
                                    botMapping.model.endsWith(m.name) ||
                                    (botMapping.model.includes("/") &&
                                      m.name.endsWith(
                                        botMapping.model.split("/").pop()!,
                                      )) ||
                                    (m.name.includes("/") &&
                                      botMapping.model.endsWith(
                                        m.name.split("/").pop()!,
                                      )),
                                );
                                if (match) {
                                  displayModelName = match.displayName;
                                  if (!displayIcon) {
                                    displayIcon = PROVIDERS.find(
                                      (p) => p.id === prov,
                                    )?.icon;
                                  }
                                  break;
                                }
                              }
                            }

                            const isImpacted = !!impactedBotIds?.has(
                              bot.botCustomId,
                            );
                            const showProviderDropdown = true;

                            return (
                              <tr
                                key={bot.botCustomId}
                                className="byok-table-row"
                                style={
                                  isImpacted
                                    ? {
                                        outlineOffset: "-1px",
                                        borderRadius: 8,
                                        background: "#FFF8F8",
                                      }
                                    : undefined
                                }
                              >
                                <td className="byok-table-col-check">
                                  <input
                                    type="checkbox"
                                    checked={selectedBots.has(bot.botCustomId)}
                                    onChange={() => {
                                      setSelectedBots((prev) => {
                                        const next = new Set(prev);
                                        if (next.has(bot.botCustomId))
                                          next.delete(bot.botCustomId);
                                        else next.add(bot.botCustomId);
                                        return next;
                                      });
                                    }}
                                    className="byok-checkbox"
                                  />
                                </td>
                                <td className="byok-table-col-name byok-bot-name-text">
                                  <div className="flex w-full min-w-0 items-center gap-[6px]">
                                    <span
                                      className="inline-block w-[187px] min-w-0 max-w-[187px] overflow-hidden whitespace-nowrap"
                                      title={
                                        bot.projectName || "Untitled Agent"
                                      }
                                      onMouseEnter={(e) => {
                                        const container = e.currentTarget;
                                        const content = container.querySelector(
                                          ".marquee-content",
                                        ) as HTMLElement;
                                        if (!content) return;
                                        const overflow =
                                          content.scrollWidth -
                                          container.clientWidth;
                                        if (overflow > 0) {
                                          const duration = overflow / 40;
                                          content.style.transition = `transform ${duration}s linear`;
                                          content.style.transform = `translateX(-${overflow}px)`;
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        const content =
                                          e.currentTarget.querySelector(
                                            ".marquee-content",
                                          ) as HTMLElement;
                                        if (!content) return;
                                        content.style.transition =
                                          "transform 0.3s ease";
                                        content.style.transform =
                                          "translateX(0)";
                                      }}
                                    >
                                      <span className="marquee-content">
                                        {bot.projectName || "Untitled Agent"}
                                      </span>
                                    </span>
                                    {isImpacted && (
                                      <span
                                        className="inline-flex w-[261px] items-center gap-[3px] rounded-full px-[7px] py-[2px] text-[10px] font-medium"
                                        style={{
                                          background: "#FDECEA",
                                          color: "#E03B2D",
                                        }}
                                        title="Your BYOK keys don't include a key for this agent's current model provider. Please remap it."
                                      >
                                        <svg
                                          width="9"
                                          height="9"
                                          viewBox="0 0 16 16"
                                          fill="none"
                                        >
                                          <path
                                            d="M8 1.5a6.5 6.5 0 1 0 0 13A6.5 6.5 0 0 0 8 1.5ZM0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8Z"
                                            fill="#E03B2D"
                                          />
                                          <path
                                            d="M7.25 4.75a.75.75 0 0 1 1.5 0v3.5a.75.75 0 0 1-1.5 0v-3.5ZM8 11.25a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"
                                            fill="#E03B2D"
                                          />
                                        </svg>
                                        No matching key
                                      </span>
                                    )}
                                  </div>
                                </td>

                                {/* Model Dropdown */}
                                <td className="byok-table-col-model byok-dropdown-container">
                                  <div className="flex w-full items-center gap-[6px]">
                                    {showProviderDropdown && (
                                      <>
                                        <div
                                          onMouseDown={(e) =>
                                            e.stopPropagation()
                                          }
                                          onClick={(e) => {
                                            if (
                                              openDropdownBotId ===
                                                bot.botCustomId &&
                                              openDropdownType === "config"
                                            ) {
                                              setOpenDropdownBotId(null);
                                              setOpenDropdownType(null);
                                            } else {
                                              const rect =
                                                e.currentTarget.getBoundingClientRect();
                                              setDropdownPos({
                                                top: rect.bottom + 4,
                                                left: rect.left,
                                                width:
                                                  rect.width < 150
                                                    ? 150
                                                    : rect.width,
                                              });
                                              setOpenDropdownBotId(
                                                bot.botCustomId,
                                              );
                                              setOpenDropdownType("config");
                                            }
                                          }}
                                          className={`byok-dropdown-trigger hi w-[50%] ${openDropdownBotId === bot.botCustomId && openDropdownType === "config" ? "byok-dropdown-trigger--open" : ""}`}
                                        >
                                          {displayIcon && (
                                            <img
                                              src={displayIcon}
                                              alt=""
                                              className="byok-dropdown-icon"
                                            />
                                          )}
                                          <span
                                            className="byok-dropdown-label flex items-center gap-[6px] overflow-hidden"
                                            onMouseEnter={(e) => {
                                              const container = e.currentTarget;
                                              const text =
                                                container.querySelector(
                                                  ".byok-marquee-text",
                                                ) as HTMLElement;
                                              if (
                                                text &&
                                                text.scrollWidth >
                                                  container.clientWidth
                                              ) {
                                                const overflow =
                                                  text.scrollWidth -
                                                  container.clientWidth;
                                                text.style.transition = `transform ${Math.max(overflow * 15, 600)}ms ease-in-out`;
                                                text.style.transform = `translateX(-${overflow}px)`;
                                              }
                                            }}
                                            onMouseLeave={(e) => {
                                              const text =
                                                e.currentTarget.querySelector(
                                                  ".byok-marquee-text",
                                                ) as HTMLElement;
                                              if (text) {
                                                text.style.transition =
                                                  "transform 300ms ease-in-out";
                                                text.style.transform =
                                                  "translateX(0)";
                                              }
                                            }}
                                          >
                                            <span className="byok-marquee-text text-[12px] font-medium text-[#444]">
                                              {botConfig
                                                ? botConfig.name
                                                : botMapping.provider
                                                  ? `${PROVIDERS.find((p) => p.id === botMapping.provider)?.label} `
                                                  : "Select Provider "}
                                            </span>
                                          </span>

                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className={`byok-dropdown-chevron ${openDropdownBotId === bot.botCustomId && openDropdownType === "config" ? "byok-dropdown-chevron--open" : ""}`}
                                            width="10"
                                            height="6"
                                            viewBox="0 0 10 6"
                                            fill="none"
                                          >
                                            <path
                                              d="M0.666992 0.666687L4.66699 4.66669L8.66699 0.666687"
                                              stroke="#666666"
                                              stroke-width="1.33333"
                                              stroke-linecap="round"
                                              stroke-linejoin="round"
                                            />
                                          </svg>
                                        </div>

                                        {/* Config Menu Portal */}
                                        {openDropdownBotId ===
                                          bot.botCustomId &&
                                          openDropdownType === "config" &&
                                          createPortal(
                                            <div
                                              ref={dropdownMenuRef}
                                              className="byok-dropdown-menu"
                                              style={{
                                                position: "fixed",
                                                top: dropdownPos.top,
                                                left: dropdownPos.left,
                                                width: 220,
                                              }}
                                            >
                                              {/* <SearchBar
                                              value={dropdownSearch}
                                              onChange={setDropdownSearch}
                                              placeholder="Search configs..."
                                              wrapperClassName="byok-dropdown-search"
                                              autoFocus
                                              onClick={(e) => e.stopPropagation()}
                                            /> */}
                                              {dropdownSearch === "" && (
                                                <>
                                                  {!byok &&
                                                    !savedConfigId &&
                                                    !isImpacted &&
                                                    mode !== "onboarding" &&
                                                    mode !== "add" && (
                                                      <>
                                                        <div className="byok-dropdown-label-group px-3 py-1 text-[10px] font-bold uppercase text-gray-400">
                                                          Platform Providers
                                                        </div>
                                                        {PROVIDERS.filter(
                                                          (p) =>
                                                            p.id !==
                                                            "openrouter",
                                                        ).map((p) => {
                                                          const isSelected =
                                                            !botMapping.configId &&
                                                            botMapping.provider ===
                                                              p.id;
                                                          return (
                                                            <div
                                                              key={`platform-${p.id}`}
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                setBotMap(
                                                                  (prev) => ({
                                                                    ...prev,
                                                                    [bot.botCustomId]:
                                                                      {
                                                                        ...prev[
                                                                          bot
                                                                            .botCustomId
                                                                        ],
                                                                        configId:
                                                                          "", // Clear config for platform
                                                                        provider:
                                                                          p.id,
                                                                        model:
                                                                          getBestModelForProvider(
                                                                            p.id,
                                                                            prev[
                                                                              bot
                                                                                .botCustomId
                                                                            ]
                                                                              ?.model,
                                                                          ),
                                                                      },
                                                                  }),
                                                                );
                                                                setOpenDropdownBotId(
                                                                  null,
                                                                );
                                                                setOpenDropdownType(
                                                                  null,
                                                                );
                                                                setDropdownSearch(
                                                                  "",
                                                                );
                                                              }}
                                                              className={`byok-dropdown-option ${isSelected ? "byok-dropdown-option--selected" : ""}`}
                                                            >
                                                              <img
                                                                src={p.icon}
                                                                alt=""
                                                                className="byok-dropdown-icon mr-2 h-4 w-4"
                                                              />
                                                              <span className="byok-dropdown-option-label truncate">
                                                                {p.label}
                                                              </span>
                                                            </div>
                                                          );
                                                        })}
                                                      </>
                                                    )}
                                                  {(byok ||
                                                    isImpacted ||
                                                    mode === "onboarding" ||
                                                    mode === "add") && (
                                                    <div className="byok-dropdown-label-group mt-1 px-3 py-1 text-[10px] font-bold uppercase text-gray-400">
                                                      {isImpacted && !byok
                                                        ? "Available Keys"
                                                        : "Your Custom Keys"}
                                                    </div>
                                                  )}
                                                </>
                                              )}
                                              {(byok ||
                                                isImpacted ||
                                                mode === "onboarding" ||
                                                mode === "add") &&
                                              existingConfigs.length === 0 ? (
                                                <div className="nothing-found text-align-center flex h-full w-full items-center justify-center text-[12px] text-[#444]">
                                                  No custom keys configured yet.
                                                </div>
                                              ) : (
                                                (byok ||
                                                  isImpacted ||
                                                  mode === "onboarding" ||
                                                  mode === "add") &&
                                                (() => {
                                                  const filtered =
                                                    existingConfigs.filter(
                                                      (cfg) =>
                                                        cfg.name
                                                          .toLowerCase()
                                                          .includes(
                                                            dropdownSearch.toLowerCase(),
                                                          ),
                                                    );
                                                  if (
                                                    filtered.length === 0 &&
                                                    dropdownSearch
                                                  ) {
                                                    return (
                                                      <div className="nothing-found text-align-center flex h-full w-full items-center justify-center text-[12px] text-[#444]">
                                                        No matching
                                                        configurations found
                                                      </div>
                                                    );
                                                  }
                                                  return filtered.map((cfg) => {
                                                    const isSelected =
                                                      botMapping.configId ===
                                                      cfg._id;
                                                    return (
                                                      <div
                                                        key={cfg._id}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          const newProvider =
                                                            cfg.provider;
                                                          setBotMap((prev) => ({
                                                            ...prev,
                                                            [bot.botCustomId]: {
                                                              ...prev[
                                                                bot.botCustomId
                                                              ],
                                                              configId: cfg._id,
                                                              provider:
                                                                newProvider,
                                                              model:
                                                                getModelForConfigSelection(
                                                                  cfg,
                                                                  prev[
                                                                    bot
                                                                      .botCustomId
                                                                  ]?.model,
                                                                ),
                                                            },
                                                          }));
                                                          setOpenDropdownBotId(
                                                            null,
                                                          );
                                                          setOpenDropdownType(
                                                            null,
                                                          );
                                                          setDropdownSearch("");
                                                        }}
                                                        className={`byok-dropdown-option ${isSelected ? "byok-dropdown-option--selected" : ""}`}
                                                      >
                                                        <span className="byok-dropdown-option-label truncate">
                                                          {cfg.name}
                                                        </span>
                                                      </div>
                                                    );
                                                  });
                                                })()
                                              )}
                                            </div>,
                                            modalRef.current!,
                                          )}

                                        <span className="text-[#bbb]">/</span>
                                      </>
                                    )}

                                    {/* Model Dropdown */}
                                    <div
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onClick={(e) => {
                                        const isModelDisabled =
                                          mode !== "onboarding" &&
                                          !botMapping.configId &&
                                          !botMapping.provider; // Allow if provider is set (platform)
                                        if (isModelDisabled) return;
                                        if (
                                          openDropdownBotId ===
                                            bot.botCustomId &&
                                          openDropdownType === "model"
                                        ) {
                                          setOpenDropdownBotId(null);
                                          setOpenDropdownType(null);
                                        } else {
                                          const rect =
                                            e.currentTarget.getBoundingClientRect();
                                          const dropdownHeight = 290;
                                          const spaceBelow =
                                            window.innerHeight - rect.bottom;
                                          const openAbove =
                                            spaceBelow < dropdownHeight &&
                                            rect.top > spaceBelow;
                                          setDropdownPos({
                                            top: openAbove
                                              ? rect.top - dropdownHeight - 4
                                              : rect.bottom + 4,
                                            left: rect.left,
                                            width:
                                              rect.width < 180
                                                ? 180
                                                : rect.width,
                                          });
                                          setOpenDropdownBotId(bot.botCustomId);
                                          setOpenDropdownType("model");
                                        }
                                      }}
                                      title={
                                        isModelDisabled
                                          ? "Please select a configuration or platform provider first"
                                          : undefined
                                      }
                                      className={`byok-dropdown-trigger w-full ${openDropdownBotId === bot.botCustomId && openDropdownType === "model" ? "byok-dropdown-trigger--open" : ""} ${isModelDisabled ? "byok-dropdown-trigger--disabled" : ""}`}
                                    >
                                      <span className="byok-dropdown-label flex items-center gap-[6px] truncate">
                                        <span
                                          className="overflow-hidden whitespace-nowrap text-[13px] text-[#666]"
                                          style={{
                                            textOverflow: "ellipsis",
                                            flex: 1,
                                          }}
                                          onMouseEnter={(e) => {
                                            const container = e.currentTarget;
                                            const content =
                                              container.querySelector(
                                                ".marquee-content",
                                              ) as HTMLElement;
                                            if (!content) return;
                                            const overflow =
                                              content.scrollWidth -
                                              container.clientWidth;
                                            if (overflow > 0) {
                                              const duration = overflow / 40;
                                              content.style.transition = `transform ${duration}s linear`;
                                              content.style.transform = `translateX(-${overflow}px)`;
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            const content =
                                              e.currentTarget.querySelector(
                                                ".marquee-content",
                                              ) as HTMLElement;
                                            if (!content) return;
                                            content.style.transition =
                                              "transform 0.3s ease";
                                            content.style.transform =
                                              "translateX(0)";
                                          }}
                                        >
                                          <span
                                            className="marquee-content text-[12px]"
                                            style={{
                                              display: "inline-block",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {displayModelName || "Select Model"}
                                          </span>
                                        </span>
                                      </span>
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className={`byok-dropdown-chevron ${openDropdownBotId === bot.botCustomId && openDropdownType === "model" ? "byok-dropdown-chevron--open" : ""}`}
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                      >
                                        <path
                                          d="M4 6L8 10L12 6"
                                          stroke="#666666"
                                          stroke-width="1.33333"
                                          stroke-linecap="round"
                                          stroke-linejoin="round"
                                        />
                                      </svg>
                                    </div>

                                    {/* Model Menu Portal */}
                                    {openDropdownBotId === bot.botCustomId &&
                                      openDropdownType === "model" &&
                                      createPortal(
                                        <div
                                          ref={dropdownMenuRef}
                                          className="byok-dropdown-menu hello"
                                          style={{
                                            position: "fixed",
                                            top: dropdownPos.top,
                                            left: dropdownPos.left,
                                            width: dropdownPos.width,
                                          }}
                                        >
                                          <SearchBar
                                            value={dropdownSearch}
                                            onChange={setDropdownSearch}
                                            placeholder="Search models..."
                                            wrapperClassName="byok-dropdown-search"
                                            autoFocus
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                          <div className="byok-dropdown-options-wrapper grey-scrollbar pt-[5px]">
                                            {pModels.length === 0 ? (
                                              <div className="nothing-found text-align-center flex h-full w-full items-center justify-center text-[12px] text-[#666]">
                                                No models available.
                                              </div>
                                            ) : (
                                              (() => {
                                                const filtered = pModels.filter(
                                                  (model) =>
                                                    model.displayName
                                                      .toLowerCase()
                                                      .includes(
                                                        dropdownSearch.toLowerCase(),
                                                      ),
                                                );
                                                if (
                                                  filtered.length === 0 &&
                                                  dropdownSearch
                                                ) {
                                                  return (
                                                    <div className="nothing-found text-align-center flex h-full w-full items-center justify-center text-[12px] text-[#444]">
                                                      No matching models found
                                                    </div>
                                                  );
                                                }
                                                return filtered.map(
                                                  (model, idx) => {
                                                    const isSelected =
                                                      botMapping.model ===
                                                      model.name;

                                                    // Restriction logic
                                                    const isAllowed =
                                                      isSelected ||
                                                      plan?.name === "Trial" ||
                                                      plan?.name ===
                                                        "outgrow" ||
                                                      plan?.name ===
                                                        "Lifetime" ||
                                                      (model.name.startsWith(
                                                        "auto",
                                                      ) &&
                                                        (allowedModels?.includes(
                                                          "auto",
                                                        ) ||
                                                          allowedModels?.some(
                                                            (am: string) =>
                                                              normalize(am) ===
                                                              "auto",
                                                          ))) ||
                                                      allowedModels?.some(
                                                        (am: string) => {
                                                          const nm = normalize(
                                                            model.name,
                                                          );
                                                          const na =
                                                            normalize(am);
                                                          return (
                                                            nm === na ||
                                                            model.name.toLowerCase() ===
                                                              am.toLowerCase()
                                                          );
                                                        },
                                                      );

                                                    const isDisabled = false;
                                                    // !botMapping.configId &&
                                                    // !isAllowed;

                                                    return (
                                                      <div
                                                        key={model.name}
                                                        onClick={(e) => {
                                                          if (isDisabled)
                                                            return;
                                                          e.stopPropagation();
                                                          setBotMap((prev) => ({
                                                            ...prev,
                                                            [bot.botCustomId]: {
                                                              ...prev[
                                                                bot.botCustomId
                                                              ],
                                                              model: model.name,
                                                            },
                                                          }));
                                                          setOpenDropdownBotId(
                                                            null,
                                                          );
                                                          setOpenDropdownType(
                                                            null,
                                                          );
                                                          setDropdownSearch("");
                                                        }}
                                                        className={`byok-dropdown-option ${isSelected ? "byok-dropdown-option--selected" : ""} ${isDisabled ? "byok-dropdown-option--disabled" : ""}`}
                                                      >
                                                        {pIcon && (
                                                          <img
                                                            src={pIcon}
                                                            alt=""
                                                            className="byok-dropdown-option-icon"
                                                          />
                                                        )}
                                                        <span
                                                          className="byok-dropdown-option-label"
                                                          onMouseEnter={(e) => {
                                                            const container =
                                                              e.currentTarget;
                                                            const content =
                                                              container.querySelector(
                                                                ".marquee-content",
                                                              ) as HTMLElement;
                                                            if (!content)
                                                              return;
                                                            const overflow =
                                                              content.scrollWidth -
                                                              container.clientWidth;
                                                            if (overflow > 0) {
                                                              const duration =
                                                                overflow / 40;
                                                              content.style.transition = `transform ${duration}s linear`;
                                                              content.style.transform = `translateX(-${overflow}px)`;
                                                            }
                                                          }}
                                                          onMouseLeave={(e) => {
                                                            const content =
                                                              e.currentTarget.querySelector(
                                                                ".marquee-content",
                                                              ) as HTMLElement;
                                                            if (!content)
                                                              return;
                                                            content.style.transition =
                                                              "transform 0.3s ease";
                                                            content.style.transform =
                                                              "translateX(0)";
                                                          }}
                                                        >
                                                          <span className="marquee-content">
                                                            {model.displayName}
                                                            {idx === 0 &&
                                                            selectedProvider !==
                                                              "auto"
                                                              ? " "
                                                              : ""}
                                                          </span>
                                                        </span>
                                                        {isDisabled && (
                                                          <svg
                                                            width="12"
                                                            height="12"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="2.5"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            className="ml-auto text-gray-400"
                                                          >
                                                            <rect
                                                              x="3"
                                                              y="11"
                                                              width="18"
                                                              height="11"
                                                              rx="2"
                                                              ry="2"
                                                            />
                                                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                                          </svg>
                                                        )}
                                                        {isSelected && (
                                                          <svg
                                                            width="14"
                                                            height="14"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            className="byok-dropdown-option-check flex-shrink-0"
                                                          >
                                                            <path
                                                              d="M5 13L9 17L19 7"
                                                              stroke="#FB5F66"
                                                              strokeWidth="2.5"
                                                              strokeLinecap="round"
                                                              strokeLinejoin="round"
                                                            />
                                                          </svg>
                                                        )}
                                                      </div>
                                                    );
                                                  },
                                                );
                                              })()
                                            )}
                                          </div>
                                        </div>,
                                        modalRef.current!,
                                      )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── Footer (sticky via grid) ── */}
          <div className="byok-onb-footer">
            {step === 1 && (
              <div className="byok-footer">
                {isCustomModelFormOpen &&
                mode !== "edit" &&
                !isAddingModelToExistingKey ? (
                  <button
                    className="byok-back-btn"
                    onClick={handleBackFromCustomModelForm}
                    disabled={loading}
                  >
                    Back
                  </button>
                ) : showBackToDashboardButton ? (
                  <button
                    className="byok-back-btn"
                    onClick={handleBackToDashboard}
                    disabled={loading || isReturningToDashboard}
                  >
                    {isReturningToDashboard
                      ? "Returning to dashboard..."
                      : "Back to dashboard"}
                  </button>
                ) : null}
                <button
                  className={`byok-continue-btn ${!canProceedStep1 || loading ? "byok-btn-disabled" : ""}`}
                  disabled={!canProceedStep1 || loading}
                  onClick={handleNext}
                >
                  {loading
                    ? isAddingModelToExistingKey
                      ? "Adding Model..."
                      : mode === "edit"
                        ? "Saving Changes..."
                        : "Saving Key..."
                    : isAddingModelToExistingKey
                      ? "Add Model"
                      : mode === "edit"
                        ? "Save Changes"
                        : isCustomModelFormOpen
                          ? "Save Model"
                          : "Continue"}
                </button>
              </div>
            )}
            {step === 2 && (
              <div className="byok-footer">
                {mode !== "edit" && (
                  <button
                    onClick={() => {
                      // If there's no Step 1 (edit mode or impact-check forced remap), just close.
                      // IMPORTANT: For regular onboarding, transition to Step 1 to PRESERVE botMap state.
                      if (impactedBotIds?.size && mode !== "onboarding") {
                        onClose();
                      } else {
                        setStep(1);
                      }
                    }}
                    disabled={saving}
                    className="byok-back-btn"
                  >
                    Back
                  </button>
                )}

                {botsLoading ? (
                  <div
                    className="byok-loading-btn-skeleton"
                    aria-hidden="true"
                  />
                ) : (
                  <button
                    disabled={saving}
                    onClick={handleFinish}
                    className={`byok-continue-btn ${
                      saving ||
                      (impactedBotIds &&
                        bots.some(
                          (bot) =>
                            impactedBotIds.has(bot.botCustomId) &&
                            !botMap[bot.botCustomId]?.configId,
                        ))
                        ? "byok-btn-disabled"
                        : ""
                    }`}
                  >
                    {saving
                      ? "Saving..."
                      : byok
                        ? isBasicOrProOrTrialPlan || isLegacyAppSumoUser
                          ? "Finish and Save AI Provider"
                          : "Finish and Enable BYOK"
                        : "Finish and Save"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        {/* Close button — hidden during forced remapping or when dismiss is blocked */}
        {!impactedBotIds && (!hideCloseButton || canDismissBuilderGate) && (
          <button
            onClick={onClose}
            title="Close"
            disabled={isReturningToDashboard}
            className="byok-close-floating"
            style={{ background: "white" }}
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 10 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 1.00714L8.99286 0L5 3.99286L1.00714 0L0 1.00714L3.99286 5L0 8.99286L1.00714 10L5 6.00714L8.99286 10L10 8.99286L6.00714 5L10 1.00714Z"
                fill="#6D6D6D"
              ></path>
            </svg>
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default ByokOnboarding;
